import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import RichTextEditor from './components/RichTextEditor';
import LyricAIPopup from './LyricAIPopup';
import { authService } from './services/authService';

// Add keyframe animation for spinning
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

/**
 * Convert JIRA ADF (Atlassian Document Format) to readable HTML text
 * @param {object|string} adf - JIRA ADF object or string
 * @returns {string} - Readable HTML text
 */
function convertAdfToHtml(adf) {
  if (!adf) return '';
  
  // If it's already a string, return it
  if (typeof adf === 'string') return adf;
  
  // If it's not an ADF object, return empty
  if (!adf.type || !adf.content) return '';
  
  let result = '';
  
  function processContent(content) {
    if (!Array.isArray(content)) return '';
    
    return content.map(node => {
      if (!node) return '';
      
      switch (node.type) {
        case 'heading':
          const level = node.attrs?.level || 1;
          const headingContent = processContent(node.content || []);
          return `<h${level}>${headingContent}</h${level}>`;
        
        case 'paragraph':
          const paraContent = processContent(node.content || []);
          return `<p>${paraContent}</p>`;
        
        case 'bulletList':
        case 'orderedList':
          const listItems = (node.content || []).map(item => {
            const itemContent = processContent(item.content || []);
            return `<li>${itemContent}</li>`;
          }).join('');
          const listTag = node.type === 'bulletList' ? 'ul' : 'ol';
          return `<${listTag}>${listItems}</${listTag}>`;
        
        case 'listItem':
          return processContent(node.content || []);
        
        case 'text':
          let text = node.text || '';
          // Apply marks (bold, italic, etc.)
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type === 'strong') text = `<strong>${text}</strong>`;
              if (mark.type === 'em') text = `<em>${text}</em>`;
              if (mark.type === 'code') text = `<code>${text}</code>`;
            });
          }
          return text;
        
        case 'codeBlock':
          const codeContent = processContent(node.content || []);
          return `<pre><code>${codeContent}</code></pre>`;
        
        case 'blockquote':
          const quoteContent = processContent(node.content || []);
          return `<blockquote>${quoteContent}</blockquote>`;
        
        default:
          return processContent(node.content || []);
      }
    }).join('');
  }
  
  result = processContent(adf.content);
  return result;
}


const IssueForm = forwardRef(({ selectedProject, onProjectChange, selectedModel, userInfo, issueTypes = [] }, ref) => {
  const [mode, setMode] = useState('add'); // 'add' or 'edit'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [allIssues, setAllIssues] = useState([]);
  const [allIssuesTotal, setAllIssuesTotal] = useState(0); // Track total count from server
  const [loadingAllIssues, setLoadingAllIssues] = useState(false);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState([]);
  const [suggestedStories, setSuggestedStories] = useState([]);
  const [isSuggestingStories, setIsSuggestingStories] = useState(false);
  const [expandedStories, setExpandedStories] = useState({});
  
  const [issueType, setIssueType] = useState(''); // Will be set when issue types load
  const [storyName, setStoryName] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionHistory, setDescriptionHistory] = useState(''); // Track description before AI paste
  // Estimation fields (Effort (Days), Story Points). Loaded from JIRA on issue
  // select and editable on the form. `*FieldId` is the JIRA custom-field ID we
  // need to PUT to on save — captured from the fetch so we don't hardcode it.
  // `*Editable` reflects whether the field is on the project's edit screen —
  // when false we disable the input and show an admin-fix tooltip.
  const [effortDays, setEffortDays] = useState('');
  const [effortDaysFieldId, setEffortDaysFieldId] = useState(null);
  const [effortDaysEditable, setEffortDaysEditable] = useState(true);
  const [storyPoints, setStoryPoints] = useState('');
  const [storyPointsFieldId, setStoryPointsFieldId] = useState(null);
  const [storyPointsEditable, setStoryPointsEditable] = useState(true);
  // AI estimate widget state
  const [estimateDevCount, setEstimateDevCount] = useState(1);
  const [estimateUsingAi, setEstimateUsingAi] = useState(true);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const [estimateRationale, setEstimateRationale] = useState('');
  // capacityImpact from the estimate response. Shape: { percentOfWeek,
  // storyPointsAvailable, devCount }. Null when the server couldn't load
  // the estimation config (graceful degradation).
  const [estimateCapacity, setEstimateCapacity] = useState(null);
  const [embeddedReviewFromJira, setEmbeddedReviewFromJira] = useState(null); // Track review that was embedded in description when loaded from JIRA
  const [epicKey, setEpicKey] = useState('');
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEpics, setLoadingEpics] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [reviewResult, setReviewResult] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isApplyingFix, setIsApplyingFix] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateManuallySet, setTemplateManuallySet] = useState(false); // Track if user manually changed template
  const [showClearConfirm, setShowClearConfirm] = useState(false); // For custom confirmation modal
  const [showClearDescriptionConfirm, setShowClearDescriptionConfirm] = useState(false); // For clear description confirmation
  const [showAiHelperConfirm, setShowAiHelperConfirm] = useState(false); // For AI helper warnings
  const [aiHelperConfirmData, setAiHelperConfirmData] = useState(null); // Data for AI helper confirmation
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false); // For submit confirmation
  const [submitConfirmType, setSubmitConfirmType] = useState(null); // 'no-review', 'needs-clarification', 'create', 'update'
  const [submitConfirmData, setSubmitConfirmData] = useState(null);
  const [excludeWarningData, setExcludeWarningData] = useState(false); // Checkbox to exclude warning data from ticket
  const [showUpdateSuccessModal, setShowUpdateSuccessModal] = useState(false); // For update success modal
  const [updateSuccessData, setUpdateSuccessData] = useState(null); // Data for update success modal
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState(''); // Track workflow status selection
  const [availableStatuses, setAvailableStatuses] = useState([]); // Available workflow statuses for the project
  const [imageLinks, setImageLinks] = useState(''); // Image URLs (comma or newline separated)
  const [figmaLink, setFigmaLink] = useState(''); // Figma design link
  const [referenceSourceUrl, setReferenceSourceUrl] = useState(''); // Reference source URL
  const [referenceSourceFile, setReferenceSourceFile] = useState(null); // Reference source file
  const [referenceSourceFileName, setReferenceSourceFileName] = useState(''); // Reference source file name
  const [jiraProductDiscoveryViews, setJiraProductDiscoveryViews] = useState([]); // JIRA Product Discovery views
  const [loadingJiraViews, setLoadingJiraViews] = useState(false); // Loading state for JIRA views
  const [selectedJiraIdea, setSelectedJiraIdea] = useState(null); // Selected idea
  const [jiraIdeasFilter, setJiraIdeasFilter] = useState(''); // Search filter for ideas
  const [slackChannel, setSlackChannel] = useState(''); // Slack channel for notifications
  const [jamaLink, setJamaLink] = useState(''); // Jama link for requirements
  const [githubBranch, setGithubBranch] = useState(''); // GitHub branch for the issue
  const [repositories, setRepositories] = useState([]); // Available GitHub repositories
  const [selectedRepo, setSelectedRepo] = useState(''); // Selected repository
  const [repoFilter, setRepoFilter] = useState(''); // Filter/search for repositories
  const [showRepoList, setShowRepoList] = useState(false); // Show/hide repo dropdown
  const [branches, setBranches] = useState([]); // Branches for selected repository
  const [branchFilter, setBranchFilter] = useState(''); // Filter/search for branches
  const [showBranchList, setShowBranchList] = useState(false); // Show/hide branch dropdown
  const [loadingRepos, setLoadingRepos] = useState(false); // Loading state for repos
  const [loadingBranches, setLoadingBranches] = useState(false); // Loading state for branches
  const [customRulesFileName, setCustomRulesFileName] = useState(''); // Name of the custom rules file
  const [customRulesData, setCustomRulesData] = useState(null); // Parsed custom rules JSON
  const [githubBranchUrl, setGithubBranchUrl] = useState(''); // Pasted GitHub URL for branch
  const [showBranchUrlInput, setShowBranchUrlInput] = useState(false); // Show branch URL input
  const [treeStructure, setTreeStructure] = useState([]); // Tree structure from GitHub
  const [loadingTreeStructure, setLoadingTreeStructure] = useState(false); // Loading tree structure
  const [selectedTreeItem, setSelectedTreeItem] = useState(null); // Selected file/directory
  const [expandedTreeNodes, setExpandedTreeNodes] = useState({}); // Track expanded folders
  
  // Collapsible section states
  const [expandedSections, setExpandedSections] = useState({
    github: false,
    jama: false,
    figma: false,
    images: false,
    slack: false,
    reference: false,
    referenceSource: false,
    jiraProductDiscovery: false
  }); // Track which sections are expanded

  // Ref for RichTextEditor to get selected text
  const richTextEditorRef = useRef(null);
  const fileInputRef = useRef(null);
  const customRulesInputRef = useRef(null); // Ref for custom rules file input
  const storyDescriptionRefs = useRef({}); // Refs for each story description editor
  const repoDropdownRef = useRef(null); // Ref for repository dropdown
  const branchDropdownRef = useRef(null); // Ref for branch dropdown

  const isFormEnabled = !!selectedProject;

  // Helper function to toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close repo dropdown if click is outside
      if (repoDropdownRef.current && !repoDropdownRef.current.contains(event.target)) {
        setShowRepoList(false);
      }
      // Close branch dropdown if click is outside
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setShowBranchList(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function for authenticated API calls
  const authenticatedFetch = async (url, options = {}) => {
    try {
      const token = await authService.getAccessToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      };
      
      return fetch(url, {
        ...options,
        headers
      });
    } catch (error) {
      console.error('Authentication error:', error);
      throw new Error('Failed to authenticate. Please log in again.');
    }
  };

  // Set default issue type when issue types are loaded
  useEffect(() => {
    if (issueTypes && issueTypes.length > 0) {
      // Check if there's a last selected type in localStorage
      const lastSelectedType = localStorage.getItem('lastSelectedIssueType');
      
      if (lastSelectedType) {
        // Try to find the last selected type
        const savedType = issueTypes.find(type => type.id === lastSelectedType);
        if (savedType) {
          setIssueType(savedType.id);
          return;
        }
      }
      
      // Default to Epic first, otherwise use first available
      const epicType = issueTypes.find(type => type.name.toLowerCase() === 'epic');
      if (epicType) {
        setIssueType(epicType.id);
      } else {
        setIssueType(issueTypes[0].id);
      }
    }
  }, [issueTypes]);

  // Save selected issue type to localStorage whenever it changes
  useEffect(() => {
    if (issueType) {
      localStorage.setItem('lastSelectedIssueType', issueType);
    }
  }, [issueType]);

  useEffect(() => {
    // Reset form state when project changes
    setStoryName('');
    setDescription('');
    setEpicKey('');
    setEpics([]);
    setResult(null);
    setError(null);
    setImageLinks('');
    setFigmaLink('');
  }, []);

  // Define fetchEpics function outside of useEffect so it can be called by parent
  const fetchEpics = useCallback(async () => {
    if (selectedProject) {
      setLoadingEpics(true);
      try {
        const apiUrl = getApiBase();
        const response = await authenticatedFetch(`${apiUrl}/api/epics?projectKey=${selectedProject}`);
        const data = await response.json();
        if (data.success) {
          setEpics(data.epics);
          console.log('[ISSUE-FORM] 🔄 Epics refreshed:', data.epics.length);
        } else {
          throw new Error(data.error || 'Failed to fetch epics.');
        }
      } catch (err) {
        setError(`Error fetching epics: ${err.message}`);
      } finally {
        setLoadingEpics(false);
      }
    } else {
      setEpics([]);
    }
  }, [selectedProject]);

  // Fetch epics when project changes
  useEffect(() => {
    fetchEpics();
  }, [selectedProject, fetchEpics]);

  // Expose refreshEpics method to parent component via forwardRef
  useImperativeHandle(ref, () => ({
    refreshEpics: () => {
      fetchEpics();
    }
  }), [fetchEpics]);

  // Fetch available JSON templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const apiUrl = getApiBase();
        const response = await authenticatedFetch(`${apiUrl}/api/templates`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.templates) {
            setAvailableTemplates(data.templates);
          }
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
    };
    fetchTemplates();
  }, []);

  // Initialize selectedTemplate based on issue type (only when user hasn't manually changed it)
  useEffect(() => {
    // Only auto-sync template with issue type when user hasn't manually selected a different template
    if (issueTypes && issueTypes.length > 0 && issueType && !templateManuallySet && availableTemplates.length > 0) {
      const issueTypeName = issueTypes.find(t => t.id === issueType)?.name;
      if (issueTypeName && issueTypeName !== selectedTemplate) {
        // Check if a template exists for this issue type, otherwise default to Story
        const templateExists = availableTemplates.some(t => 
          t.toLowerCase() === issueTypeName.toLowerCase()
        );
        const templateToUse = templateExists ? issueTypeName : 'Story';
        setSelectedTemplate(templateToUse);
        
        if (!templateExists) {
          console.log(`📋 No template found for "${issueTypeName}", defaulting to Story template`);
        }
      }
    }
  }, [issueType, issueTypes, templateManuallySet, selectedTemplate, availableTemplates]);

  // Fetch available workflow statuses for the project
  useEffect(() => {
    const fetchStatuses = async () => {
      if (!selectedProject) {
        console.log('[WORKFLOW] No project selected, skipping status fetch');
        return;
      }
      
      console.log('[WORKFLOW] Fetching statuses for project:', selectedProject);
      
      try {
        const apiUrl = getApiBase();
        const url = `${apiUrl}/api/project/${selectedProject}/statuses`;
        console.log('[WORKFLOW] Fetching from URL:', url);
        
        const response = await authenticatedFetch(url);
        console.log('[WORKFLOW] Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('[WORKFLOW] Response data:', data);
          
          if (data.success && data.statuses) {
            console.log('[WORKFLOW] Found all statuses:', data.statuses);
            
            // Find "To Do" and "Ready for Review" statuses
            const todoStatus = data.statuses.find(s => 
              s.name.toLowerCase() === 'to do' || 
              s.name.toLowerCase() === 'todo' ||
              s.name.toLowerCase() === 'open'
            );
            const readyStatus = data.statuses.find(s => 
              s.name.toLowerCase().includes('ready for review') || 
              s.name.toLowerCase().includes('ready')
            );
            
            // Only show "To Do" by default
            // "Ready for Review" will be added only when ticket is approved
            const filteredStatuses = todoStatus ? [todoStatus] : [];
            
            console.log('[WORKFLOW] Filtered statuses (only To Do initially):', filteredStatuses);
            setAvailableStatuses(filteredStatuses);
            
            // Set default to "To Do"
            if (todoStatus) {
              console.log('[WORKFLOW] Setting default status to To Do:', todoStatus.name, 'ID:', todoStatus.id);
              setSelectedWorkflowStatus(todoStatus.id);
            } else {
              console.warn('[WORKFLOW] No To Do status found');
            }
            
            // Store the ready status for later use
            if (readyStatus) {
              console.log('[WORKFLOW] Ready for Review status available:', readyStatus.name);
            }
          } else {
            console.warn('[WORKFLOW] Invalid response format:', data);
          }
        } else {
          console.error('[WORKFLOW] Failed to fetch statuses, status:', response.status);
        }
      } catch (err) {
        console.error('[WORKFLOW] Error fetching statuses:', err);
        setAvailableStatuses([]);
      }
    };
    
    fetchStatuses();
  }, [selectedProject]);

  // Update available statuses when review result changes or when issue is selected in edit mode
  useEffect(() => {
    const updateStatusesBasedOnReview = async () => {
      if (!selectedProject) return;
      
      console.log('[WORKFLOW] Updating available statuses - mode:', mode, 'reviewResult:', reviewResult?.status);
      
      try {
        const apiUrl = getApiBase();
        const url = `${apiUrl}/api/project/${selectedProject}/statuses`;
        const response = await authenticatedFetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.statuses) {
            const todoStatus = data.statuses.find(s => 
              s.name.toLowerCase() === 'to do' || 
              s.name.toLowerCase() === 'todo' ||
              s.name.toLowerCase() === 'open'
            );
            const readyStatus = data.statuses.find(s => 
              s.name.toLowerCase().includes('ready for review') || 
              s.name.toLowerCase().includes('ready')
            );
            
            // Build filtered list based on review state and mode
            const filteredStatuses = todoStatus ? [todoStatus] : [];
            
            // Handle "Ready for Review" status availability
            if (reviewResult) {
              // If review has been performed
              if (reviewResult.status === 'approved') {
                // Ticket approved - add "Ready for Review" and auto-select it
                if (readyStatus && !filteredStatuses.find(s => s.id === readyStatus.id)) {
                  filteredStatuses.push(readyStatus);
                  setSelectedWorkflowStatus(readyStatus.id);
                  console.log('[WORKFLOW] ✅ Approved - added Ready for Review and auto-selected');
                }
              } else {
                // Review says NO - remove "Ready for Review" even in edit mode, force to "To Do"
                if (todoStatus) {
                  setSelectedWorkflowStatus(todoStatus.id);
                  console.log('[WORKFLOW] ❌ Review failed - forcing status to To Do');
                }
              }
            } else {
              // No review performed yet
              if (mode === 'edit' && selectedIssue && selectedIssue.fields.status) {
                // In edit mode without review, preserve existing "Ready for Review" status if present
                const currentStatusName = selectedIssue.fields.status.name.toLowerCase();
                if ((currentStatusName.includes('ready for review') || currentStatusName.includes('ready')) && readyStatus) {
                  if (!filteredStatuses.find(s => s.id === readyStatus.id)) {
                    filteredStatuses.push(readyStatus);
                  }
                  console.log('[WORKFLOW] 📝 Edit mode without review - preserving existing Ready for Review status');
                }
              } else if (mode === 'add' && todoStatus) {
                // No review in add mode - default to "To Do"
                setSelectedWorkflowStatus(todoStatus.id);
                console.log('[WORKFLOW] ⚠️ No review in add mode - defaulting to To Do');
              }
            }
            
            console.log('[WORKFLOW] Updated available statuses:', filteredStatuses);
            setAvailableStatuses(filteredStatuses);
          }
        }
      } catch (err) {
        console.error('[WORKFLOW] Error updating statuses based on review:', err);
      }
    };
    
    updateStatusesBasedOnReview();
  }, [reviewResult, selectedProject, mode, selectedIssue]);

  // Fetch GitHub repositories
  useEffect(() => {
    const fetchRepositories = async () => {
      setLoadingRepos(true);
      try {
        const apiUrl = getApiBase();
        const response = await authenticatedFetch(`${apiUrl}/api/github/repos`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.repos) {
            // Transform repositories to get owner name and repo name
            const formattedRepos = data.repos.map((repo) => ({
              id: repo.id,
              name: repo.name,
              owner: typeof repo.owner === 'object' ? repo.owner.login : repo.owner,
              fullName: repo.full_name || `${typeof repo.owner === 'object' ? repo.owner.login : repo.owner}/${repo.name}`
            }));
            setRepositories(formattedRepos);
            console.log(`✅ Fetched ${formattedRepos.length} GitHub repositories`);
          } else {
            console.warn('Failed to fetch repositories:', data.error);
            setRepositories([]);
          }
        } else {
          console.warn('Failed to fetch repositories, status:', response.status);
          setRepositories([]);
        }
      } catch (err) {
        console.warn('Error fetching GitHub repositories:', err);
        setRepositories([]);
      } finally {
        setLoadingRepos(false);
      }
    };

    fetchRepositories();
  }, []);

  // Fetch branches when selected repository changes
  useEffect(() => {
    if (!selectedRepo) {
      setBranches([]);
      setGithubBranch('');
      return;
    }

    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const apiUrl = getApiBase();
        const response = await authenticatedFetch(`${apiUrl}/api/github/repos/${selectedRepo}/branches`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.branches) {
            setBranches(data.branches);
            console.log(`✅ Fetched ${data.branches.length} branches for repository ${selectedRepo}`);
          } else {
            console.warn('Failed to fetch branches:', data.error);
            setBranches([]);
          }
        } else {
          console.warn('Failed to fetch branches, status:', response.status);
          setBranches([]);
        }
      } catch (err) {
        console.warn('Error fetching GitHub branches:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [selectedRepo]);

  // Handle pasting GitHub branch URL
  const handleGithubBranchUrlPaste = async (e) => {
    const url = e.target.value;
    setGithubBranchUrl(url);

    if (!url.trim()) return;

    // Parse GitHub URL - supports formats like:
    // https://github.com/owner/repo/tree/branch-name
    // https://github.com/owner/repo/blob/branch-name/path/to/file
    const githubUrlRegex = /github\.com\/([^/]+)\/([^/]+)\/(?:tree|blob)\/([^/]+)/;
    const match = url.match(githubUrlRegex);

    if (match) {
      const [, owner, repo, branchName] = match;
      const repoFullName = `${owner}/${repo}`;

      console.log(`🔗 Parsed GitHub URL - Repo: ${repoFullName}, Branch: ${branchName}`);

      // Set the repository and branch
      setSelectedRepo(repoFullName);
      setGithubBranch(branchName);
      setBranchFilter(branchName);

      // Fetch tree structure for this branch
      await fetchTreeStructure(repoFullName, branchName);
    } else {
      console.warn('Invalid GitHub URL format');
    }
  };

  // Fetch tree structure from GitHub branch
  const fetchTreeStructure = async (repoFullName, branchName) => {
    setLoadingTreeStructure(true);
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(
        `${apiUrl}/api/github/repos/${repoFullName}/contents?ref=${branchName}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.contents) {
          setTreeStructure(data.contents);
          console.log(`✅ Fetched tree structure with ${data.contents.length} items`);
        } else {
          console.warn('Failed to fetch tree structure:', data.error);
          setTreeStructure([]);
        }
      } else {
        console.warn('Failed to fetch tree structure, status:', response.status);
        setTreeStructure([]);
      }
    } catch (err) {
      console.warn('Error fetching GitHub tree structure:', err);
      setTreeStructure([]);
    } finally {
      setLoadingTreeStructure(false);
    }
  };

  // Toggle folder expansion in tree
  const toggleTreeNode = (path) => {
    setExpandedTreeNodes(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Fetch JIRA Product Discovery Ideas (all ideas from PM project)
  const fetchJiraProductDiscoveryViews = useCallback(async () => {
    setLoadingJiraViews(true);
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/jira/product-discovery/views?projectKey=PM`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.ideas) {
          // Store ideas directly instead of views
          setJiraProductDiscoveryViews(data.ideas);
          console.log(`✅ Loaded ${data.ideas.length} JIRA Product Discovery Ideas`);
        } else {
          console.warn('Failed to fetch JIRA ideas:', data.error);
          setJiraProductDiscoveryViews([]);
        }
      } else {
        console.warn('Failed to fetch JIRA ideas, status:', response.status);
        setJiraProductDiscoveryViews([]);
      }
    } catch (err) {
      console.warn('Error fetching JIRA Product Discovery ideas:', err);
      setJiraProductDiscoveryViews([]);
    } finally {
      setLoadingJiraViews(false);
    }
  }, []);

  // Fetch ideas when section expands
  useEffect(() => {
    if (expandedSections.jiraProductDiscovery && jiraProductDiscoveryViews.length === 0) {
      fetchJiraProductDiscoveryViews();
    }
  }, [expandedSections.jiraProductDiscovery, jiraProductDiscoveryViews.length, fetchJiraProductDiscoveryViews]);

  // Fetch all issues when switching to edit mode
  useEffect(() => {
    const fetchAllIssues = async () => {
      if (mode === 'edit' && selectedProject) {
        setLoadingAllIssues(true);
        setError(null);
        try {
          const apiUrl = getApiBase();
          const response = await authenticatedFetch(`${apiUrl}/api/search-issues?projectKey=${selectedProject}&query=`);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.success) {
            setAllIssues(data.issues || []);
            setAllIssuesTotal(data.total || data.issues?.length || 0);
            console.log(`✅ Loaded ${data.issues?.length || 0} issues (total: ${data.total || data.issues?.length || 0}) for project ${selectedProject}`);
          } else {
            setError(data.error || 'Failed to fetch issues');
          }
        } catch (err) {
          console.error('[FETCH-ISSUES] Error:', err);
          setError(`Error fetching issues: ${err.message}`);
        } finally {
          setLoadingAllIssues(false);
        }
      }
    };
    fetchAllIssues();
  }, [mode, selectedProject]);

  const handleGenerateDescription = async () => {
    if (!storyName) return;

    // Always run generation - no caching, allow free re-runs
    // AI will re-read both title and user description each time
    continueGenerateDescription();
  };

  const continueGenerateDescription = async () => {
    const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Story';
    
    setIsGeneratingDescription(true);
    setError(null);
    try {
      const apiUrl = getApiBase();
      
      console.log(`🔄 Re-running AI description generator for: "${storyName}"`);
      console.log(`📝 Current user description: ${description && description.length > 0 ? `${description.length} characters` : 'empty'}`);
      
      // Use the rules-based generation endpoint
      // Always send the current description so AI can re-analyze and improve it
      const requestBody = {
        summary: storyName,
        issueType: issueTypeName,
        selectedModel: selectedModel,
        existingDescription: (typeof description === 'string' && description.trim()) ? description : null,
        rulesTemplate: selectedTemplate || issueTypeName,
      };
      
      // Send customRules if user has uploaded a custom file
      if (customRulesData) {
        requestBody.customRules = customRulesData;
        console.log(`[GENERATE-DESC] Sending custom rules from file upload: ${customRulesFileName}`);
      }
      
      const response = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Server responded with status ${response.status}`;
        
        // Try to get error details
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          // Got HTML or other non-JSON response
          const textResponse = await response.text();
          console.error('Non-JSON response received:', textResponse.substring(0, 200));
          errorMessage = response.status === 404 
            ? 'API endpoint not found. Please ensure the backend server is running and the /api/ai/generate-description-from-rules endpoint is implemented.'
            : 'Server error occurred. Please check the backend server logs.';
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        // Combine user input with AI description
        let combinedDescription = '';
        if (data.userInput && data.userInput.trim()) {
          // User had input - show both user input and AI enhancement
          combinedDescription = `<p><b>📝 Your Input:</b></p>\n${data.userInput}\n<p><b>✨ AI Enhancement:</b></p>\n${data.description}`;
        } else {
          // No user input, just show AI description
          combinedDescription = data.description;
        }
        
        // Set the combined description
        setDescription(combinedDescription);
        console.log(`✨ Description regenerated using ${data.rulesApplied} rules with ${data.modelUsed}`);
      } else {
        throw new Error(data.error || 'Failed to generate description.');
      }
    } catch (err) {
      console.error('Error generating description:', err);
      setError('Could not generate description. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Ask the backend's Gemini-backed estimator for an Effort (days) + Story
  // Points pair given the current title/description/issue type plus the
  // user-picked context (number of devs, whether they'll use AI assistants).
  // Writes the returned values straight into the inputs so the user can
  // tweak before saving — does NOT submit to JIRA. The actual JIRA write
  // happens on the existing Update flow.
  const runEstimate = async () => {
    setEstimateError('');
    setEstimateRationale('');
    setEstimating(true);
    try {
      const apiUrl = getApiBase();
      // Resolve the issue type name we currently have selected — Gemini cares
      // about Story vs Bug vs Task vs Epic when sizing the work.
      const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || (selectedIssue?.fields?.issuetype?.name) || 'Story';
      const res = await authenticatedFetch(`${apiUrl}/api/ai/estimate-issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: storyName,
          description,
          issueType: issueTypeName,
          devCount: estimateDevCount,
          usingAi: estimateUsingAi,
          selectedModel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Estimate failed (HTTP ${res.status})`);
      if (typeof data.days === 'number') setEffortDays(String(data.days));
      if (typeof data.storyPoints === 'number') setStoryPoints(String(data.storyPoints));
      if (data.rationale) setEstimateRationale(String(data.rationale));
      if (data.capacityImpact && typeof data.capacityImpact.percentOfWeek === 'number') {
        setEstimateCapacity(data.capacityImpact);
      } else {
        setEstimateCapacity(null);
      }
    } catch (err) {
      console.error('[ESTIMATE] Error:', err);
      setEstimateError(err.message);
    } finally {
      setEstimating(false);
    }
  };

  const handleReviewDescription = async (templateOverride = null) => {
    if (!description) {
      setReviewResult({ 
        status: 'needs_clarification',
        rulesApplied: selectedTemplate || issueTypes.find(t => t.id === issueType)?.name || 'Story',
        modelUsed: selectedModel || 'Not specified'
      });
      return;
    }

    setIsReviewing(true);
    setReviewResult(null); // Clear previous result

    try {
      const apiUrl = getApiBase();
      
      console.log('🔍 [REVIEW-DEBUG] ========== BEFORE COMPUTING issueTypeName ==========');
      console.log('🔍 [REVIEW-DEBUG] templateOverride:', templateOverride, typeof templateOverride);
      console.log('🔍 [REVIEW-DEBUG] selectedTemplate:', selectedTemplate, typeof selectedTemplate);
      console.log('🔍 [REVIEW-DEBUG] issueTypes.find():', issueTypes.find(t => t.id === issueType), typeof issueTypes.find(t => t.id === issueType));
      console.log('🔍 [REVIEW-DEBUG] issueTypes.find()?.name:', issueTypes.find(t => t.id === issueType)?.name, typeof issueTypes.find(t => t.id === issueType)?.name);
      
      const issueTypeName = templateOverride || selectedTemplate || issueTypes.find(t => t.id === issueType)?.name || 'Story';
      
      console.log('🔍 [REVIEW-DEBUG] ========== AFTER COMPUTING issueTypeName ==========');
      console.log('🔍 [REVIEW-DEBUG] issueTypeName:', issueTypeName, typeof issueTypeName);
      console.log('🔍 [REVIEW-DEBUG] String(issueTypeName):', String(issueTypeName));
      console.log('🔍 [REVIEW-DEBUG] ================================================');
      
      // Use the actual issue key when editing an existing issue, otherwise 'NEW'
      const issueKey = mode === 'edit' && selectedIssue?.key ? selectedIssue.key : 'NEW';
      
      // Extract acceptance criteria from description if available
      const acceptanceCriteriaText = acceptanceCriteria.length > 0 
        ? acceptanceCriteria.join('\n') 
        : '';
      
      // Determine which template was used for generation
      let usedTemplate = selectedTemplate || issueTypeName;
      if (customRulesFileName) {
        usedTemplate = `Custom: ${customRulesFileName}`;
      }
      
      const payload = {
        issueKey: String(issueKey || 'NEW'),
        summary: String(storyName || ''),
        description: String(description || ''),
        acceptanceCriteria: String(acceptanceCriteriaText || ''),
        issueType: String(issueTypeName || 'Story'),
        selectedModel: String(selectedModel || 'gemini-2.5-flash'),
        figmaLink: String(figmaLink || ''),
        rulesTemplate: usedTemplate, // Include the template that was used to generate this
      };
      
      // Send custom rules if user uploaded a file
      if (customRulesData) {
        payload.customRules = customRulesData;
        console.log('🔍 [REVIEW] Using custom rules from file:', customRulesFileName);
      }
      
      console.log('🔍 [REVIEW] ========== SENDING TO BACKEND ==========');
      console.log('🔍 [REVIEW] payload.issueType:', payload.issueType);
      console.log('🔍 [REVIEW] typeof payload.issueType:', typeof payload.issueType);
      console.log('🔍 [REVIEW] Full payload:', payload);
      console.log('🔍 [REVIEW] ================================================');
      
      const response = await authenticatedFetch(`${apiUrl}/api/story/review`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      console.log('📥 [REVIEW] Received response:', {
        success: data.success,
        status: data.status,
        isGood: data.isGood,
        message: data.message,
        rulesApplied: data.rulesApplied,
        modelUsed: data.modelUsed,
        fullResponse: data
      });
      
      if (data.success) {
        // Backend returns the review data directly in the response, not nested in data.review
        const review = {
          status: data.status || 'needs_clarification',
          isGood: data.isGood || false,
          message: data.message || 'No feedback provided',
          rulesApplied: data.rulesApplied || issueTypeName,
          modelUsed: data.modelUsed || selectedModel || 'Not specified',
          // Parse issues and recommendations from message if they exist
          issues: data.issues || [],
          recommendations: data.recommendations || []
        };
        
        // If no structured issues/recommendations, parse from message
        if (review.issues.length === 0 && review.message) {
          review.issues = review.status === 'needs_clarification' 
            ? [review.message]
            : [];
        }
        
        setReviewResult(review);
      } else {
        setReviewResult({ 
          status: 'needs_clarification',
          issues: [data.error || 'Failed to get review from server'],
          recommendations: ['Check console for errors and try again'],
          rulesApplied: issueTypeName,
          modelUsed: selectedModel || 'Not specified'
        });
      }
    } catch (err) {
      console.error('Error reviewing description:', err);
      const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Story';
      setReviewResult({ 
        status: 'needs_clarification',
        rulesApplied: issueTypeName,
        modelUsed: selectedModel || 'Not specified'
      });
    } finally {
      setIsReviewing(false);
    }
  };

  const appendReviewToDescription = (desc, skipReview = false) => {
    // IMPORTANT: Do NOT append the review to the description!
    // The review should only be displayed in the UI, not saved to JIRA
    // This prevents the raw markdown from appearing in the description when the ticket is reloaded
    
    console.log('[APPEND-REVIEW] Review metadata is NOT appended to description (UI display only)');
    return desc;
  };

  const handleClearForm = () => {
    setShowClearConfirm(true);
  };

  const confirmClearForm = () => {
    setStoryName('');
    setDescription('');
    setDescriptionHistory('');
    setEmbeddedReviewFromJira(null); // Clear any embedded review
    setEpicKey('');
    setReviewResult(null);
    setError(null);
    setResult(null);
    setSelectedIssue(null);
    setSearchQuery('');
    setSuggestedStories([]);
    setAcceptanceCriteria([]);
    setImageLinks('');
    setFigmaLink('');
    setReferenceSourceUrl('');
    setReferenceSourceFile(null);
    setReferenceSourceFileName('');
    setSlackChannel('');
    setJamaLink('');
    setGithubBranch('');
    setSelectedRepo('');
    setRepoFilter('');
    setShowRepoList(false);
    setBranchFilter('');
    setShowBranchList(false);
    setBranches('');
    setExcludeWarningData(false); // Reset the checkbox
    setCustomRulesFileName('');
    setCustomRulesData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (customRulesInputRef.current) {
      customRulesInputRef.current.value = '';
    }
    setShowClearConfirm(false);
  };

  const cancelClearForm = () => {
    setShowClearConfirm(false);
  };

  const confirmSubmit = async () => {
    setShowSubmitConfirm(false);
    setLoading(true);
    
    // Now actually perform the submission
    await performSubmit();
  };

  const cancelSubmit = () => {
    setShowSubmitConfirm(false);
    setSubmitConfirmType(null);
    setSubmitConfirmData(null);
  };

  // State for validation error popup
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationError, setShowValidationError] = useState(false);

  /**
   * Validate payload structure before sending to server
   * @param {object} payload - The payload to validate
   * @returns {array} - Array of error messages (empty if valid)
   */
  const validatePayload = (payload) => {
    const errors = [];

    // Check required fields
    if (!payload.summary || payload.summary.trim() === '') {
      errors.push('Summary/Title is required');
    }

    if (!payload.description || (typeof payload.description === 'string' && payload.description.trim() === '')) {
      errors.push('Description is required');
    }

    // If description is an object (ADF format), validate its structure
    if (payload.description && typeof payload.description === 'object') {
      if (!payload.description.type || payload.description.type !== 'doc') {
        errors.push('Invalid description format - expected ADF document type');
      }
      
      if (!Array.isArray(payload.description.content)) {
        errors.push('Invalid description format - missing content array');
      }

      // Check for empty lists that could cause JIRA errors
      if (Array.isArray(payload.description.content)) {
        payload.description.content.forEach((node, idx) => {
          if ((node.type === 'bulletList' || node.type === 'orderedList') && 
              (!node.content || node.content.length === 0)) {
            errors.push(`Empty list found at section ${idx + 1} - please add items or remove the list`);
          }
        });
      }
    }

    // Check for stories array if present (Epic with stories)
    if (Array.isArray(payload.stories)) {
      payload.stories.forEach((story, idx) => {
        if (!story.summary || story.summary.trim() === '') {
          errors.push(`Story ${idx + 1}: Summary is required`);
        }
      });
    }

    // Log validation results
    if (errors.length === 0) {
      console.log('[VALIDATION] ✅ Payload validation passed');
    } else {
      console.log('[VALIDATION] ❌ Validation failed with errors:', errors);
      console.log('[VALIDATION] Problematic payload:', JSON.stringify(payload, null, 2));
    }

    return errors;
  };

  /**
   * Show validation error popup
   */
  const showValidationErrorPopup = (errors) => {
    setValidationErrors(errors);
    setShowValidationError(true);
  };

  /**
   * Copy error details to clipboard
   */
  const copyErrorDetailsToClipboard = () => {
    const errorText = `Payload Validation Errors:\n\n${validationErrors.map((err, i) => `${i + 1}. ${err}`).join('\n')}\n\nPlease include these details when reporting to the developer.`;
    
    navigator.clipboard.writeText(errorText).then(() => {
      alert('✅ Error details copied to clipboard. Please send to developer.');
    }).catch(err => {
      console.error('[VALIDATION] Failed to copy to clipboard:', err);
      alert('❌ Failed to copy. Please manually note the errors above.');
    });
  };

  const performSubmit = async () => {
    // Check if we're in edit mode
    if (mode === 'edit') {
      const updateData = {
        issueKey: selectedIssue.key,
        summary: storyName,
        description: appendReviewToDescription(description, excludeWarningData),
        epicKey,
        issueType: issueType,
        imageLinks: imageLinks,
        figmaLink: figmaLink,
        // Estimation fields. Only sent when the field is BOTH configured on
        // this JIRA instance AND editable on this project's edit screen.
        // Skipping fields the project can't accept avoids the noisy 400 we
        // used to surface for projects without these fields on their screen.
        ...(storyPointsFieldId !== null && storyPointsEditable ? { storyPoints } : {}),
        ...(effortDaysFieldId !== null && effortDaysEditable ? { effortDays } : {}),
      };

      // Validate payload before sending
      const validationErrs = validatePayload(updateData);
      if (validationErrs.length > 0) {
        showValidationErrorPopup(validationErrs);
        setLoading(false);
        return;
      }

      try {
        console.log('[CLIENT] ===== UPDATING ISSUE =====');
        console.log('[CLIENT] Issue Key:', updateData.issueKey);
        console.log('[CLIENT] Summary:', updateData.summary);
        console.log('[CLIENT] Description length:', updateData.description?.length || 0, 'chars');
        if (imageLinks) console.log('[CLIENT] Image Links:', imageLinks);
        if (figmaLink) console.log('[CLIENT] Figma Link:', figmaLink);
        console.log('[CLIENT] Full Update Data:', JSON.stringify(updateData, null, 2));
        console.log('[CLIENT] 📤 Sending to /api/update-issue...');
        console.log('[CLIENT] ===== END UPDATE DATA =====');
        
        const apiUrl = getApiBase();
        const response = await authenticatedFetch(`${apiUrl}/api/update-issue`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Server returns a structured payload for the "field not on screen"
          // case — concatenate the error + actionable hint so the user knows
          // who to ask and what to ask for.
          if (errorData.fieldsNotOnScreen) {
            throw new Error(`${errorData.error}\n\n${errorData.hint || ''}`.trim());
          }
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const jiraResult = await response.json();

        console.log('[CLIENT] ✅ Update successful:', jiraResult);
        
        setUpdateSuccessData({
          issueKey: jiraResult.key || selectedIssue.key,
          url: jiraResult.link
        });
        setShowUpdateSuccessModal(true);
        
        setResult({
          ...jiraResult,
          jiraCreated: false,
          jiraUpdated: true,
          issueKey: jiraResult.key || selectedIssue.key,
          url: jiraResult.link,
        });
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setStoryName('');
          setDescription('');
          setEpicKey('');
          setIssueType(issueTypes[0]?.id || 'Task');
          setSelectedIssue(null);
          setSearchQuery('');
          setReviewResult(null);
          setError(null);
          setImageLinks('');
          setFigmaLink('');
        }, 3000);
        
        // Auto-hide success message after 15 seconds
        setTimeout(() => {
          setResult(null);
        }, 15000);
        
      } catch (err) {
        console.error('[CLIENT] ❌ Error updating issue:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Original create logic for Add mode
    // Check if creating an Epic with suggested stories
    const isEpic = issueTypes.find(t => t.id === issueType)?.name.toLowerCase() === 'epic';
    const storiesToCreate = suggestedStories.filter(s => s.include);
    const hasStories = storiesToCreate.length > 0;

    try {
      const apiUrl = getApiBase();
      let response, endpoint, payload;

      if (isEpic && hasStories) {
        // Creating Epic with Stories - use create-epic endpoint
        endpoint = `${apiUrl}/api/create-epic`;
        payload = {
          summary: storyName,
          description: appendReviewToDescription(description, excludeWarningData),
          projectKey: selectedProject,
          stories: storiesToCreate,
          selectedModel: selectedModel,
          reporterEmail: userInfo?.email,
          reporterName: userInfo?.name,
          initialStatus: selectedWorkflowStatus,
          imageLinks: imageLinks,
          figmaLink: figmaLink,
        };
        
        // Validate payload before sending
        const validationErrs = validatePayload(payload);
        if (validationErrs.length > 0) {
          showValidationErrorPopup(validationErrs);
          setLoading(false);
          return;
        }
        
        console.log('[CLIENT] ===== CREATING EPIC WITH STORIES =====');
        console.log('[CLIENT] Epic Summary:', storyName);
        console.log('[CLIENT] Description length:', payload.description?.length || 0, 'chars');
        console.log('[CLIENT] Stories to create:', storiesToCreate.length);
        storiesToCreate.forEach((story, idx) => {
          console.log(`[CLIENT]   Story ${idx + 1}: "${story.summary}"`);
          if (story.acceptanceCriteria?.length > 0) {
            console.log(`[CLIENT]     - Acceptance Criteria: ${story.acceptanceCriteria.length} items`);
          }
        });
        if (imageLinks) console.log('[CLIENT] Image Links:', imageLinks);
        if (figmaLink) console.log('[CLIENT] Figma Link:', figmaLink);
        console.log('[CLIENT] Full Payload:', JSON.stringify(payload, null, 2));
        console.log('[CLIENT] 📤 Sending to /api/create-epic...');
        console.log('[CLIENT] ===== END EPIC PAYLOAD =====');
        
        response = await authenticatedFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Creating single issue (including Epic without stories) - use create-story endpoint
        endpoint = `${apiUrl}/api/create-story`;
        payload = {
          summary: storyName,
          description: appendReviewToDescription(description, excludeWarningData),
          epicKey,
          projectKey: selectedProject,
          issueType: issueType,
          reporterEmail: userInfo?.email,
          reporterName: userInfo?.name,
          initialStatus: selectedWorkflowStatus,
          imageLinks: imageLinks,
          figmaLink: figmaLink,
        };
        
        // Validate payload before sending
        const validationErrs = validatePayload(payload);
        if (validationErrs.length > 0) {
          showValidationErrorPopup(validationErrs);
          setLoading(false);
          return;
        }
        
        const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Unknown';
        console.log('[CLIENT] ===== CREATING ISSUE =====');
        console.log('[CLIENT] Issue Type:', issueTypeName);
        console.log('[CLIENT] Project:', selectedProject);
        console.log('[CLIENT] Summary:', storyName);
        console.log('[CLIENT] Description length:', payload.description?.length || 0, 'chars');
        if (epicKey) console.log('[CLIENT] Parent Epic:', epicKey);
        if (imageLinks) console.log('[CLIENT] Image Links:', imageLinks);
        if (figmaLink) console.log('[CLIENT] Figma Link:', figmaLink);
        console.log('[CLIENT] Full Payload:', JSON.stringify(payload, null, 2));
        console.log('[CLIENT] 📤 Sending to /api/create-story...');
        console.log('[CLIENT] ===== END ISSUE PAYLOAD =====');
        
        response = await authenticatedFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const jiraResult = await response.json();
      
      console.log('[CLIENT] ===== BACKEND RESPONSE =====');
      console.log('[CLIENT] Success:', jiraResult.success);
      if (isEpic && hasStories) {
        console.log('[CLIENT] Epic Key:', jiraResult.epicKey);
        console.log('[CLIENT] Stories Created:', jiraResult.stories?.length || 0);
        if (jiraResult.stories) {
          jiraResult.stories.forEach((story, idx) => {
            console.log(`[CLIENT]   Story ${idx + 1}: ${story.key} - ${story.summary}`);
          });
        }
      } else {
        console.log('[CLIENT] Issue Key:', jiraResult.key);
      }
      console.log('[CLIENT] ===== END RESPONSE =====');
      
      // Handle response based on whether it's an Epic with stories or single issue
      if (isEpic && hasStories) {
        // Epic with stories response format
        setResult({
          ...jiraResult,
          jiraCreated: jiraResult.success,
          issueKey: jiraResult.epicKey,  // Use epicKey for Epic
          url: jiraResult.epicLink,      // Use epicLink for Epic
          isEpic: true,
          storiesCreated: jiraResult.stories?.length || 0
        });
        
        // Reset form fields including suggested stories after 3 seconds
        setTimeout(() => {
          setStoryName('');
          setDescription('');
          setEpicKey('');
          setIssueType(issueTypes[0]?.id || 'Task');
          setReviewResult(null);
          setError(null);
          setSuggestedStories([]);
          setImageLinks('');
          setFigmaLink('');
        }, 3000);
      } else {
        // Single issue response format
        setResult({
          ...jiraResult,
          jiraCreated: jiraResult.success,
          issueKey: jiraResult.key,  // Map key to issueKey
          url: jiraResult.link,       // Map link to url
        });
        
        // Reset form fields after 3 seconds
        setTimeout(() => {
          setStoryName('');
          setDescription('');
          setEpicKey('');
          setIssueType(issueTypes[0]?.id || 'Task');
          setReviewResult(null);
          setError(null);
          setImageLinks('');
          setFigmaLink('');
        }, 3000);
      }
      
      // Auto-hide success message after 15 seconds
      setTimeout(() => {
        setResult(null);
      }, 15000);
      
    } catch (err) {
      console.error('Error creating story:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFix = async () => {
    if (!reviewResult || !reviewResult.issues || reviewResult.issues.length === 0) {
      return;
    }

    if (isApplyingFix) {
      return; // Prevent multiple clicks
    }

    setIsApplyingFix(true);

    try {
      const apiUrl = getApiBase();
      const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Story';
      
      // Call AI to generate fixes for the identified issues
      const response = await authenticatedFetch(`${apiUrl}/api/story/fix-issues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentDescription: description,
          currentSummary: storyName,
          issues: reviewResult.issues,
          recommendations: reviewResult.recommendations,
          issueType: issueTypeName,
          selectedModel: selectedModel,
        }),
      });

      const data = await response.json();
      
      if (data.success && data.fixes) {
        // Apply the fixes to the form
        if (data.fixes.summary && data.fixes.summary !== storyName) {
          setStoryName(data.fixes.summary);
        }
        if (data.fixes.description && data.fixes.description !== description) {
          setDescription(data.fixes.description);
        }
        
        // Clear review result and show success message
        alert('✅ Quick fixes applied! Please review the updated content and run Review again.');
        setReviewResult(null);
      } else {
        alert('❌ Failed to generate fixes. Please try manual editing.');
      }
    } catch (err) {
      console.error('Error applying quick fix:', err);
      alert('❌ Error applying quick fix. Please try manual editing.');
    } finally {
      setIsApplyingFix(false);
    }
  };

  const handleEmailPM = () => {
    if (!reviewResult) return;

    const jiraBaseUrl = process.env.REACT_APP_JIRA_BASE_URL || 'https://ischemaview.atlassian.net';
    const lyricUrl = window.location.origin;
    const issueKey = selectedIssue?.key || 'NEW';
    const jiraLink = issueKey !== 'NEW' ? `${jiraBaseUrl}/browse/${issueKey}` : 'Not yet created';
    const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Story';
    
    // Build email body
    let emailSubject = `JIRA Ticket Review: ${storyName}`;
    if (issueKey !== 'NEW') {
      emailSubject = `JIRA Ticket Review: ${issueKey} - ${storyName}`;
    }
    
    let emailBody = `Hi Team,\n\n`;
    emailBody += `I've completed a review of the following JIRA ticket using Lyric AI:\n\n`;
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    emailBody += `📋 TICKET DETAILS\n`;
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    if (issueKey !== 'NEW') {
      emailBody += `🔗 JIRA Ticket: ${jiraLink}\n`;
    }
    emailBody += `🎯 Issue Type: ${issueTypeName}\n`;
    emailBody += `📝 Summary: ${storyName}\n\n`;
    
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    emailBody += `🤖 AI REVIEW RESULTS\n`;
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    emailBody += `Status: ${reviewResult.status === 'approved' ? '✅ APPROVED - Ready for Development' : '⚠️ NEEDS CLARIFICATION'}\n`;
    emailBody += `AI Model: ${reviewResult.modelUsed || 'Not specified'}\n`;
    emailBody += `Rules Applied: ${reviewResult.rulesApplied || 'Story'}.json\n\n`;
    
    if (reviewResult.message) {
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      emailBody += `💬 AI FEEDBACK\n`;
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
      emailBody += `${reviewResult.message}\n\n`;
    }
    
    if (reviewResult.issues && reviewResult.issues.length > 0) {
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      emailBody += `⚠️ ISSUES IDENTIFIED (${reviewResult.issues.length})\n`;
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
      reviewResult.issues.forEach((issue, idx) => {
        emailBody += `${idx + 1}. ${issue}\n`;
      });
      emailBody += `\n`;
    }
    
    if (reviewResult.recommendations && reviewResult.recommendations.length > 0) {
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      emailBody += `💡 RECOMMENDATIONS (${reviewResult.recommendations.length})\n`;
      emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
      reviewResult.recommendations.forEach((rec, idx) => {
        emailBody += `${idx + 1}. ${rec}\n`;
      });
      emailBody += `\n`;
    }
    
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    emailBody += `🔗 LINKS\n`;
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    if (issueKey !== 'NEW') {
      emailBody += `📌 View in JIRA: ${jiraLink}\n`;
    }
    emailBody += `🎵 Edit in Lyric: ${lyricUrl}\n\n`;
    
    emailBody += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    emailBody += `This review was generated using Lyric AI, an intelligent JIRA ticket assistant.\n`;
    emailBody += `Please review the feedback and address any issues before moving to development.\n\n`;
    emailBody += `Best regards,\n`;
    emailBody += `${userInfo?.name || 'Your Name'}\n`;
    
    // Create mailto link
    const mailtoLink = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Copy to clipboard as backup
    navigator.clipboard.writeText(emailBody).then(() => {
      console.log('[EMAIL-PM] Email content copied to clipboard');
    }).catch(err => {
      console.error('[EMAIL-PM] Failed to copy to clipboard:', err);
    });
    
    // Open mailto link
    window.location.href = mailtoLink;
    
    // Show success message
    alert('📧 Email draft opened! Content also copied to clipboard as backup.');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setReferenceSourceFile(file);
      setReferenceSourceFileName(file.name);
      setReferenceSourceUrl(''); // Clear URL when file is selected
      console.log('[REFERENCE-SOURCE] File selected:', file.name);
    }
  };

  const handleLoadCustomRules = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result;
          if (typeof content !== 'string') {
            setError('Failed to read file content');
            return;
          }
          
          const rulesData = JSON.parse(content);
          
          // Validate that it's a valid rules file (should have a 'rules' array)
          if (!Array.isArray(rulesData.rules)) {
            throw new Error('Invalid rules file format. Expected "rules" array.');
          }
          
          setCustomRulesFileName(file.name);
          setCustomRulesData(rulesData);
          setSelectedTemplate(`Custom: ${file.name}`);
          setTemplateManuallySet(true);
          
          console.log('[CUSTOM-RULES] Loaded custom rules from file:', file.name);
          console.log('[CUSTOM-RULES] Rules count:', rulesData.rules.length);
          setError(null);
        } catch (err) {
          console.error('[CUSTOM-RULES] Error parsing rules file:', err);
          setError(`Invalid rules file: ${err.message}`);
          setCustomRulesFileName('');
          setCustomRulesData(null);
          if (customRulesInputRef.current) {
            customRulesInputRef.current.value = '';
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClearCustomRules = () => {
    setCustomRulesFileName('');
    setCustomRulesData(null);
    if (customRulesInputRef.current) {
      customRulesInputRef.current.value = '';
    }
    // Reset template to auto-selected based on issue type
    setTemplateManuallySet(false);
    const issueTypeName = issueTypes.find(t => t.id === issueType)?.name || 'Story';
    setSelectedTemplate(issueTypeName);
  };

  const handleReloadCustomRules = () => {
    console.log('[CUSTOM-RULES] User clicked reload button - opening file picker to refresh');
    
    // Clear the input value so we can select the same file again
    if (customRulesInputRef.current) {
      customRulesInputRef.current.value = '';
      // Now trigger the file picker
      customRulesInputRef.current.click();
    }
  };

  const handleClearReferenceSource = () => {
    setReferenceSourceUrl('');
    setReferenceSourceFile(null);
    setReferenceSourceFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveWithoutReview = async (e) => {
    e.preventDefault();
    await submitToJira(true); // Skip review checks
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitToJira(false); // Include review checks
  };

  const submitToJira = async (skipReviewChecks = false) => {
    setLoading(true);
    setError(null);
    setResult(null);

    // Check if we're in edit mode
    if (mode === 'edit') {
      if (!selectedIssue) {
        setError('Please select an issue to update');
        setLoading(false);
        return;
      }

      // Check if ticket was not reviewed at all (only if not skipping review checks)
      if (!skipReviewChecks && !reviewResult) {
        setSubmitConfirmType('no-review');
        setSubmitConfirmData({ mode: 'edit' });
        setShowSubmitConfirm(true);
        setLoading(false);
        return;
      }

      // Check if ticket was reviewed and marked as not acceptable (only if not skipping review checks)
      if (!skipReviewChecks && reviewResult && reviewResult.status === 'needs_clarification') {
        setSubmitConfirmType('needs-clarification');
        setSubmitConfirmData({ 
          mode: 'edit',
          issues: reviewResult.issues 
        });
        setShowSubmitConfirm(true);
        setLoading(false);
        return;
      }

      // Confirm update
      setSubmitConfirmType('update');
      setSubmitConfirmData({ 
        issueKey: selectedIssue.key,
        summary: storyName 
      });
      setShowSubmitConfirm(true);
      setLoading(false);
      return;
    }

    // Original create logic for Add mode
    
    // Check if ticket was not reviewed at all (only if not skipping review checks)
    if (!skipReviewChecks && !reviewResult) {
      setSubmitConfirmType('no-review');
      setSubmitConfirmData({ mode: 'create' });
      setShowSubmitConfirm(true);
      setLoading(false);
      return;
    }
    
    // Check if ticket was reviewed and marked as not acceptable (only if not skipping review checks)
    if (!skipReviewChecks && reviewResult && reviewResult.status === 'needs_clarification') {
      setSubmitConfirmType('needs-clarification');
      setSubmitConfirmData({ 
        mode: 'create',
        issues: reviewResult.issues 
      });
      setShowSubmitConfirm(true);
      setLoading(false);
      return;
    }
    
    const reporterName = userInfo?.name || 'Unknown';
    const reporterEmail = userInfo?.email || 'Unknown';
    
    setSubmitConfirmType('create');
    setSubmitConfirmData({ 
      reporterName,
      reporterEmail 
    });
    setShowSubmitConfirm(true);
    setLoading(false);
    return;
  };

  const handleSelectIssue = async (issue) => {
    try {
      console.log('[HANDLE-SELECT-ISSUE] Selected issue:', issue.key);
      
      // Fetch the complete issue data from the backend
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/issue/${issue.key}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch issue: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success || !data.issue) {
        throw new Error(data.error || 'Failed to load issue data');
      }
      
      const fullIssue = data.issue;
      console.log('[HANDLE-SELECT-ISSUE] Full issue data:', fullIssue);
      
      setSelectedIssue(fullIssue);
      setStoryName(fullIssue.fields.summary);
      
      // Load description from JIRA as plain text - NO formatting conversion
      let descriptionText = '';
      if (fullIssue.fields.description) {
        // If it's a string, use it directly
        if (typeof fullIssue.fields.description === 'string') {
          descriptionText = fullIssue.fields.description;
        } else if (typeof fullIssue.fields.description === 'object') {
          // If it's ADF format, extract the raw text without converting to HTML
          // Just get all text content as-is
          descriptionText = extractTextFromADF(fullIssue.fields.description);
        }
      }
      
      // Load the raw description text into the box without any formatting
      setDescription(descriptionText);
      setEmbeddedReviewFromJira(''); // Clear any embedded review

      // Load estimation fields. The backend returns null fieldId when the
      // field isn't configured on this JIRA instance (panel hidden), and
      // editable:false when the field exists but isn't on this project's
      // edit screen (input shown but disabled with explanation).
      const sp = fullIssue.fields.storyPoints;
      const ed = fullIssue.fields.effortDays;
      setStoryPointsFieldId(sp?.fieldId || null);
      setStoryPoints(typeof sp?.value === 'number' ? String(sp.value) : '');
      setStoryPointsEditable(sp?.editable !== false);
      setEffortDaysFieldId(ed?.fieldId || null);
      setEffortDays(typeof ed?.value === 'number' ? String(ed.value) : '');
      setEffortDaysEditable(ed?.editable !== false);
      setEstimateError('');
      setEstimateRationale('');
      
      console.log('[HANDLE-SELECT-ISSUE] Description loaded (raw text, no formatting): ' + descriptionText.substring(0, 100) + '...');
      
      // Extract image links from description
      let extractedImageLinks = '';
      let extractedFigmaLink = '';
      
      if (fullIssue.fields.description?.content) {
        const content = fullIssue.fields.description.content;
        let foundImagesSection = false;
        let foundFigmaSection = false;
        const imageUrls = [];
        
        for (const node of content) {
          if (node.type === 'paragraph' && node.content) {
            const text = node.content.map(c => c.text || '').join('');
            
            // Check for "Attached Images:" section
            if (text.toLowerCase().includes('attached images:')) {
              foundImagesSection = true;
              foundFigmaSection = false;
              continue;
            }
            
            // Check for "Figma Design:" section
            if (text.toLowerCase().includes('figma design:')) {
              foundFigmaSection = true;
              foundImagesSection = false;
              continue;
            }
            
            // Extract image links
            if (foundImagesSection) {
              for (const contentNode of node.content) {
                if (contentNode.marks) {
                  const linkMark = contentNode.marks.find(m => m.type === 'link');
                  if (linkMark && linkMark.attrs && linkMark.attrs.href) {
                    imageUrls.push(linkMark.attrs.href);
                  }
                }
              }
            }
            
            // Extract Figma link
            if (foundFigmaSection) {
              for (const contentNode of node.content) {
                if (contentNode.marks) {
                  const linkMark = contentNode.marks.find(m => m.type === 'link');
                  if (linkMark && linkMark.attrs && linkMark.attrs.href) {
                    extractedFigmaLink = linkMark.attrs.href;
                    foundFigmaSection = false;
                    break;
                  }
                }
              }
            }
          }
        }
        
        extractedImageLinks = imageUrls.join('\n');
      }
      
      setImageLinks(extractedImageLinks);
      setFigmaLink(extractedFigmaLink);
      
      console.log('[HANDLE-SELECT-ISSUE] Image links:', extractedImageLinks);
      console.log('[HANDLE-SELECT-ISSUE] Figma link:', extractedFigmaLink);
      
      // Extract acceptance criteria from description
      const criteria = extractAcceptanceCriteria(descriptionText);
      setAcceptanceCriteria(criteria);
      
      setEpicKey(fullIssue.fields.epic?.key || '');
      
      // Set issue type
      const typeId = fullIssue.fields.issuetype.id;
      console.log('[HANDLE-SELECT-ISSUE] Setting issue type:', typeId, fullIssue.fields.issuetype.name);
      setIssueType(typeId);
      
      setSearchQuery('');
      setDescriptionHistory(''); // Reset undo history
      
      // Set the current workflow status of the issue
      if (fullIssue.fields.status && fullIssue.fields.status.id) {
        console.log('[WORKFLOW] Loading current status for issue:', fullIssue.key, 'Status:', fullIssue.fields.status.name);
        setSelectedWorkflowStatus(fullIssue.fields.status.id);
      }
      
      // Fetch child issues (subtasks) for this issue
      await fetchChildIssues(fullIssue.key);
      
      console.log('[HANDLE-SELECT-ISSUE] ✅ Issue loaded successfully');
    } catch (error) {
      console.error('[HANDLE-SELECT-ISSUE] Error loading issue:', error);
      setError(`Failed to load issue: ${error.message}`);
    }
  };

  // Fetch child issues/subtasks
  const fetchChildIssues = async (issueKey) => {
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/issue/${issueKey}/children`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        console.log(`✅ Loaded ${data.children?.length || 0} child issues for ${issueKey}`);
      }
    } catch (err) {
      console.error('[FETCH-CHILDREN] Error:', err);
    }
  };

  // Extract acceptance criteria from description text
  const extractAcceptanceCriteria = (text) => {
    if (!text) return [];
    
    const criteria = [];
    const lines = text.split('\n');
    let inAcSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const lowerLine = line.toLowerCase();
      
      // Check if we're entering an acceptance criteria section
      if (lowerLine.includes('acceptance criteria') || 
          lowerLine.includes('acceptance criterion') ||
          lowerLine.includes('ac:')) {
        inAcSection = true;
        continue;
      }
      
      // Check if we're leaving the AC section (empty line or new section)
      if (inAcSection && (line === '' || (lowerLine.startsWith('#') && !lowerLine.includes('acceptance')))) {
        if (line === '') continue; // Skip empty lines within AC section
        inAcSection = false;
      }
      
      // Extract criteria items (bullets, numbers, checkboxes)
      if (inAcSection || 
          line.match(/^[-*•]\s+/) || 
          line.match(/^\d+\.\s+/) ||
          line.match(/^- \[[ x]\]\s+/) ||
          (lowerLine.startsWith('given') || lowerLine.startsWith('when') || lowerLine.startsWith('then'))) {
        
        // Clean up the line
        let criterionText = line
          .replace(/^[-*•]\s+/, '')
          .replace(/^\d+\.\s+/, '')
          .replace(/^- \[[ x]\]\s+/, '')
          .trim();
        
        if (criterionText && criterionText.length > 3) {
          criteria.push(criterionText);
        }
      }
    }
    
    return criteria;
  };

  // Helper function to extract text from Atlassian Document Format (ADF)
  const extractTextFromADF = (adfDoc) => {
    if (!adfDoc || !adfDoc.content) return '';
    
    const extractText = (node) => {
      if (node.type === 'text') {
        return node.text || '';
      }
      if (node.content && Array.isArray(node.content)) {
        return node.content.map(extractText).join('');
      }
      return '';
    };
    
    return adfDoc.content
      .map(node => {
        const text = extractText(node);
        // Add line breaks between paragraphs
        if (node.type === 'paragraph' || node.type === 'heading') {
          return text + '\n';
        }
        return text;
      })
      .join('')
      .trim();
  };

  // Suggest Stories for Epic
  const handleSuggestStories = async () => {
    if (!storyName.trim() || !(typeof description === 'string' && description.trim())) {
      setError("Please fill in the Epic Summary and Description first.");
      return;
    }
    setIsSuggestingStories(true);
    setError(null);
    try {
      const apiUrl = getApiBase();
      const response = await authenticatedFetch(`${apiUrl}/api/suggest-stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: storyName,
          description: description,
          projectKey: selectedProject,
          selectedModel: selectedModel,
        }),
      });
      const data = await response.json();
      if (data.success) {
        const processedStories = data.stories.map(s => {
          let criteria = [];
          if (typeof s.acceptanceCriteria === 'string') {
            criteria = s.acceptanceCriteria
              .split('\n')
              .map(line => line.trim().replace(/^\*\s*/, ''))
              .filter(line => line);
          } else if (Array.isArray(s.acceptanceCriteria)) {
            criteria = s.acceptanceCriteria;
          }
          return {
            ...s,
            acceptanceCriteria: criteria,
            include: true,
            isManual: false  // Mark AI-suggested stories
          };
        });
        
        // Preserve any manually-created stories and append AI suggestions
        const manualStories = suggestedStories.filter(s => s.isManual);
        const allStories = [...manualStories, ...processedStories];
        
        setSuggestedStories(allStories);
        setExpandedStories(allStories.reduce((acc, _, index) => ({ ...acc, [index]: false }), {}));
        
        if (manualStories.length > 0) {
          console.log(`✅ Preserved ${manualStories.length} manually-created stories and added ${processedStories.length} AI suggestions`);
        }
      } else {
        throw new Error(data.error || 'Failed to suggest stories.');
      }
    } catch (err) {
      console.error('Error suggesting stories:', err);
      setError(err.message);
    } finally {
      setIsSuggestingStories(false);
    }
  };

  const handleToggleStory = (index) => {
    setExpandedStories(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleStoryInclusionChange = (index, include) => {
    const newStories = [...suggestedStories];
    newStories[index].include = include;
    setSuggestedStories(newStories);
  };

  const handleStorySummaryChange = (index, value) => {
    const newStories = [...suggestedStories];
    newStories[index].summary = value;
    setSuggestedStories(newStories);
  };

  const handleStoryDescriptionChange = (index, value) => {
    const newStories = [...suggestedStories];
    newStories[index].description = value;
    // Track the user's original input separately - this is what we'll send to AI
    newStories[index].userInputDescription = value;
    setSuggestedStories(newStories);
  };

  const handleStoryCriteriaChange = (storyIndex, criteriaIndex, value) => {
    const newStories = [...suggestedStories];
    newStories[storyIndex].acceptanceCriteria[criteriaIndex] = value;
    setSuggestedStories(newStories);
  };

  const handleAddCriteria = (storyIndex) => {
    const newStories = [...suggestedStories];
    if (!newStories[storyIndex].acceptanceCriteria) {
      newStories[storyIndex].acceptanceCriteria = [];
    }
    newStories[storyIndex].acceptanceCriteria.push('');
    setSuggestedStories(newStories);
  };

  const handleRemoveCriteria = (storyIndex, criteriaIndex) => {
    const newStories = [...suggestedStories];
    newStories[storyIndex].acceptanceCriteria = newStories[storyIndex].acceptanceCriteria.filter((_, i) => i !== criteriaIndex);
    setSuggestedStories(newStories);
  };

  const handleSelectAllStories = () => {
    const newStories = suggestedStories.map(story => ({ ...story, include: true }));
    setSuggestedStories(newStories);
  };

  const handleDeselectAllStories = () => {
    const newStories = suggestedStories.map(story => ({ ...story, include: false }));
    setSuggestedStories(newStories);
  };

  const handleGenerateStoryDescription = async (storyIndex) => {
    const story = suggestedStories[storyIndex];
    if (!story.summary) return;

    const newStories = [...suggestedStories];
    newStories[storyIndex].isGenerating = true;
    setSuggestedStories(newStories);

    try {
      const apiUrl = getApiBase();
      
      console.log(`🔄 Generating story description for: "${story.summary}"`);
      
      // Use the rules-based generation endpoint - EXACT SAME AS MAIN FORM
      const response = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary: story.summary,
          issueType: 'Story',
          selectedModel: selectedModel,
          context: `This story is part of Epic: ${storyName}\nEpic Description: ${description}`,
          existingDescription: (typeof story.description === 'string' && story.description.trim()) ? story.description : null,
        }),
      });

      // Check if response is OK before parsing JSON
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorMessage = `Server responded with status ${response.status}`;
        
        // Try to get error details
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          // Got HTML or other non-JSON response
          const textResponse = await response.text();
          console.error('Non-JSON response received:', textResponse.substring(0, 200));
          errorMessage = response.status === 404 
            ? 'API endpoint not found. Please ensure the backend server is running.'
            : 'Server error occurred. Please check the backend server logs.';
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        console.log(`📝 [RECEIVED] AI description: ${data.description ? `${data.description.length} chars` : 'empty'}`);
        console.log(`📝 [RECEIVED] User input: ${data.userInput ? `${data.userInput.length} chars` : 'empty'}`);
        console.log(`📝 Updating state for story index: ${storyIndex}`);
        
        // Combine user input with AI description
        let combinedDescription = '';
        if (data.userInput && data.userInput.trim()) {
          // User had input - show both user input and AI enhancement
          combinedDescription = `<p><b>📝 Your Input:</b></p>\n${data.userInput}\n<p><b>✨ AI Enhancement:</b></p>\n${data.description}`;
        } else {
          // No user input, just show AI description
          combinedDescription = data.description;
        }
        
        // Create completely new array to force re-render
        const updatedStories = suggestedStories.map((s, idx) => {
          if (idx === storyIndex) {
            return {
              ...s,
              description: combinedDescription,
              isGenerating: false
            };
          }
          return s;
        });
        
        console.log(`📝 [AFTER UPDATE] New story description: ${updatedStories[storyIndex].description ? `${updatedStories[storyIndex].description.length} chars` : 'empty'}`);
        console.log(`📝 [AFTER UPDATE] isGenerating: ${updatedStories[storyIndex].isGenerating}`);
        
        setSuggestedStories(updatedStories);
        console.log(`✨ Story description regenerated using ${data.rulesApplied} rules with ${data.modelUsed}`);
      } else {
        throw new Error(data.error || 'Failed to generate description.');
      }
    } catch (err) {
      console.error('Error generating story description:', err);
      newStories[storyIndex].isGenerating = false;
      setSuggestedStories(newStories);
    }
  };

  const handleReviewStoryDescription = async (storyIndex) => {
    const story = suggestedStories[storyIndex];
    if (!story.description) return;

    const newStories = [...suggestedStories];
    newStories[storyIndex].isReviewing = true;
    setSuggestedStories(newStories);

    try {
      const apiUrl = getApiBase();
      
      // Determine which template was used for generation
      let usedTemplate = selectedTemplate || 'Story';
      if (customRulesFileName) {
        usedTemplate = `Custom: ${customRulesFileName}`;
      }
      
      const payload = {
        issueKey: 'NEW',
        summary: story.summary || 'Untitled Story',
        description: story.description,
        acceptanceCriteria: story.acceptanceCriteria?.join('\n') || '',
        issueType: 'Story',
        selectedModel: selectedModel,
        rulesTemplate: usedTemplate, // Include the template that was used to generate this
      };
      
      // Send custom rules if user uploaded a file
      if (customRulesData) {
        payload.customRules = customRulesData;
        console.log('🔍 [STORY-REVIEW] Using custom rules from file:', customRulesFileName);
      }
      
      const response = await authenticatedFetch(`${apiUrl}/api/story/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      
      if (data.success) {
        // Parse review result
        const reviewData = {
          status: data.status || 'needs_clarification',
          isGood: data.isGood || false,
          message: data.message || 'No feedback provided',
          rulesApplied: data.rulesApplied || 'Story',
          modelUsed: data.modelUsed || selectedModel || 'Not specified',
          issues: data.issues || [],
          recommendations: data.recommendations || []
        };
        
        newStories[storyIndex].reviewResult = reviewData;
        console.log('✅ [STORY-REVIEW] Review complete - Status:', reviewData.status);
      }
      newStories[storyIndex].isReviewing = false;
      setSuggestedStories(newStories);
    } catch (err) {
      console.error('Error reviewing story description:', err);
      newStories[storyIndex].isReviewing = false;
      setSuggestedStories(newStories);
    }
  };

  const handleRemoveStory = (index) => {
    const newStories = suggestedStories.filter((_, i) => i !== index);
    setSuggestedStories(newStories);
  };

  const addNewStory = () => {
    const newStory = {
      summary: '',
      description: '',
      acceptanceCriteria: [''],
      include: true,
      isManual: true  // Mark this story as manually created so it won't be lost when suggesting
    };
    const newIndex = suggestedStories.length;
    setSuggestedStories(prev => [...prev, newStory]);
    setExpandedStories(prev => ({ ...prev, [newIndex]: true }));
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Jira Issue Manager</h2>
        <p style={styles.subtitle}>Create, edit and review epics, tasks, stories, or bugs for your project.</p>
        {!selectedProject && (
          <div style={{ color: '#ff9800', marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
            ⚠️ Please select a project to enable this form.
          </div>
        )}
      </div>

      {/* Mode Selector: Add or Edit */}
      <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => {
                setMode('add');
                setSelectedIssue(null);
                setStoryName('');
                setDescription('');
                setDescriptionHistory('');
                setEpicKey('');
                setSearchQuery('');
                setAllIssues([]);
                setAcceptanceCriteria([]);
                setError(null);
              }}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: mode === 'add' ? 'bold' : 'normal',
                backgroundColor: mode === 'add' ? '#0052cc' : '#f4f5f7',
                color: mode === 'add' ? '#fff' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ➕ Add New Issue
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('edit');
                setStoryName('');
                setDescription('');
                setDescriptionHistory('');
                setEpicKey('');
                setSelectedIssue(null);
                setSearchQuery('');
                setAcceptanceCriteria([]);
                setError(null);
              }}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: mode === 'edit' ? 'bold' : 'normal',
                backgroundColor: mode === 'edit' ? '#0052cc' : '#f4f5f7',
                color: mode === 'edit' ? '#fff' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              ✏️ Edit Existing Issue
            </button>
          </div>
          <button 
            type="button" 
            onClick={handleClearForm}
            disabled={!isFormEnabled || loading || isReviewing}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: (!isFormEnabled || loading || isReviewing) ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              opacity: (!isFormEnabled || loading || isReviewing) ? 0.7 : 1,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!loading && !isReviewing && isFormEnabled) {
                e.target.style.backgroundColor = '#5a6268';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6c757d';
            }}
            title="Clear all form fields"
          >
            🗑️ Clear Form
          </button>
        </div>
      </div>

      {/* Issue Selection Section for Edit Mode */}
      {mode === 'edit' && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f4f5f7', borderRadius: '4px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Select Issue to Edit</h3>
          
          {/* Loading/Count Info */}
          {loadingAllIssues ? (
            <p style={{ margin: '0 0 1rem 0', color: '#2196f3', fontSize: '0.9rem' }}>
              ⏳ Loading all JIRA tickets...
            </p>
          ) : (
            <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.9rem' }}>
              📋 Loaded <strong>{allIssuesTotal}</strong> total tickets from project <strong>{selectedProject}</strong>
            </p>
          )}
          
          {/* Search/Filter Input */}
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Type to filter issues by key or summary..."
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                border: '2px solid #ccc', 
                borderRadius: '4px', 
                fontSize: '1rem',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2196f3'}
              onBlur={(e) => e.target.style.borderColor = '#ccc'}
              disabled={!isFormEnabled || loadingAllIssues}
            />
            {searchQuery && (
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                Filtering {allIssues.filter(issue => {
                  if (!issue || !issue.key || !issue.fields) return false;
                  const query = searchQuery.toLowerCase();
                  return (
                    issue.key.toLowerCase().includes(query) ||
                    (issue.fields.summary && issue.fields.summary.toLowerCase().includes(query))
                  );
                }).length} tickets matching "{searchQuery}"
              </p>
            )}
          </div>

          {/* Dropdown with all issues - grouped by type */}
          <div style={{ marginBottom: '1rem' }}>
            <select
              value={selectedIssue?.key || ''}
              onChange={(e) => {
                const issue = allIssues.find(i => i.key === e.target.value);
                if (issue) handleSelectIssue(issue);
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: '#fff',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
              disabled={!isFormEnabled || loadingAllIssues}
            >
              <option value="">
                {loadingAllIssues 
                  ? '⏳ Loading all tickets...' 
                  : `-- Select a ticket (${allIssues.length} loaded${allIssuesTotal > allIssues.length ? ` of ${allIssuesTotal}` : ''}) --`}
              </option>
              {(() => {
                // Filter issues based on search query
                const filteredIssues = allIssues.filter(issue => {
                  // Safety check: ensure issue has required structure
                  if (!issue || !issue.key || !issue.fields || !issue.fields.summary || !issue.fields.issuetype) {
                    return false;
                  }
                  
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  return (
                    issue.key.toLowerCase().includes(query) ||
                    issue.fields.summary.toLowerCase().includes(query)
                  );
                });

                // Group issues by type
                const groupedIssues = filteredIssues.reduce((groups, issue) => {
                  // Safety check: ensure issue has the required structure
                  if (!issue || !issue.fields || !issue.fields.issuetype || !issue.fields.issuetype.name) {
                    console.warn('Skipping issue with incomplete data:', issue);
                    return groups;
                  }
                  
                  const type = issue.fields.issuetype.name;
                  if (!groups[type]) {
                    groups[type] = [];
                  }
                  groups[type].push(issue);
                  return groups;
                }, {});

                // Sort issue types alphabetically, with Epic first
                const sortedTypes = Object.keys(groupedIssues).sort((a, b) => {
                  if (a.toLowerCase() === 'epic') return -1;
                  if (b.toLowerCase() === 'epic') return 1;
                  return a.localeCompare(b);
                });

                // Render grouped options
                return sortedTypes.map(type => (
                  <optgroup key={type} label={`${type} (${groupedIssues[type].length})`}>
                    {groupedIssues[type].map(issue => (
                      <option key={issue.key} value={issue.key}>
                        {issue.key} - {issue.fields.summary}
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Reference Source Input Box - Collapsible */}
        <div style={{ 
          marginTop: '0.25rem',
          marginBottom: '2rem',
          border: '2px solid #dee2e6',
          borderRadius: '8px',
          overflow: 'visible'
        }}>
          {/* Collapsible Header */}
          <button
            type="button"
            onClick={() => toggleSection('referenceSource')}
            style={{
              width: '100%',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              border: 'none',
              borderBottom: expandedSections.referenceSource ? '2px solid #dee2e6' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '1rem',
              fontWeight: '600',
              color: '#333',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f2f5'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
          >
            <span>📎 Reference Source <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.5rem', fontWeight: '500' }}>(Beta)</span></span>
            <span style={{ fontSize: '1.2rem', transform: expandedSections.referenceSource ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              ▶
            </span>
          </button>

          {/* Collapsible Content */}
          {expandedSections.referenceSource && (
            <div style={{ padding: '1.5rem', backgroundColor: '#fff' }}>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem', marginTop: 0 }}>
                Add a reference URL or upload a file to provide additional context for this issue.
              </p>
          
              {/* Jama Link Input - Collapsible Section */}
              <div style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'visible' }}>
                <button
                  type="button"
              onClick={() => toggleSection('jama')}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                border: 'none',
                borderBottom: expandedSections.jama ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#333',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efefef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📋 Jama Link (optional)
              </span>
              <span style={{ fontSize: '1.2rem', transform: expandedSections.jama ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </button>
            
            {expandedSections.jama && (
              <div style={{ padding: '1rem', backgroundColor: '#fff' }}>
                <input
                  id="jama-link"
                  type="url"
                  value={jamaLink}
                  onChange={(e) => setJamaLink(e.target.value)}
                  placeholder="https://jama.company.com/requirements/..."
                  disabled={!isFormEnabled || isReviewing}
                  style={{
                    ...styles.input,
                    width: '100%',
                    boxSizing: 'border-box',
                    opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                    backgroundColor: (!isFormEnabled || isReviewing) ? '#f0f0f0' : '#fff'
                  }}
                />
                <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                  Link to the Jama requirements document or item for this issue
                </p>
              </div>
            )}
          </div>

          {/* Confluence Link Input - Collapsible Section */}
          <div style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'visible' }}>
            <button
              type="button"
              onClick={() => toggleSection('reference')}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                border: 'none',
                borderBottom: expandedSections.reference ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#333',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efefef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🔗 Confluence Link (optional)
              </span>
              <span style={{ fontSize: '1.2rem', transform: expandedSections.reference ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </button>
            
            {expandedSections.reference && (
              <div style={{ padding: '1rem', backgroundColor: '#fff' }}>
                <input
                  id="reference-url"
                  type="url"
                  value={referenceSourceUrl}
                  onChange={(e) => {
                    setReferenceSourceUrl(e.target.value);
                    if (e.target.value) {
                      setReferenceSourceFile(null);
                      setReferenceSourceFileName('');
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }
                  }}
                  placeholder="https://confluence.company.com/pages/viewpage.action?pageId=..."
                  disabled={!isFormEnabled || isReviewing || !!referenceSourceFile}
                  style={{
                    ...styles.input,
                    width: '100%',
                    boxSizing: 'border-box',
                    opacity: (!!referenceSourceFile) ? 0.6 : 1,
                    backgroundColor: (!!referenceSourceFile) ? '#f0f0f0' : '#fff'
                  }}
                />
                <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                  {referenceSourceFile ? '✓ Disabled (file selected)' : 'Enter a Confluence page URL or documentation link'}
                </p>

                {/* File Upload */}
                <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <label htmlFor="reference-file" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                    Or Upload File:
                  </label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      id="reference-file"
                      type="file"
                      onChange={handleFileUpload}
                      disabled={!isFormEnabled || isReviewing || !!referenceSourceUrl}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '2px dashed #ccc',
                        borderRadius: '4px',
                        cursor: (!isFormEnabled || isReviewing || !!referenceSourceUrl) ? 'not-allowed' : 'pointer',
                        opacity: (!!referenceSourceUrl) ? 0.6 : 1,
                        backgroundColor: (!!referenceSourceUrl) ? '#f0f0f0' : '#fff'
                      }}
                      accept=".pdf,.doc,.docx,.txt,.xlsx,.csv,.pptx,.png,.jpg,.jpeg"
                    />
                    {referenceSourceFileName && (
                      <span style={{ fontSize: '0.9rem', color: '#28a745', fontWeight: 'bold' }}>
                        ✓ {referenceSourceFileName}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                    Supported: PDF, Word, Excel, PowerPoint, Text, CSV, Images
                    {referenceSourceUrl ? ' (Disabled - URL selected)' : ''}
                  </p>
                </div>

                {/* Clear Reference Button */}
                {(referenceSourceUrl || referenceSourceFile) && (
                  <button
                    type="button"
                    onClick={handleClearReferenceSource}
                    disabled={!isFormEnabled || isReviewing}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (!isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1
                    }}
                  >
                    🗑️ Clear Reference
                  </button>
                )}
              </div>
            )}
          </div>

          {/* JIRA Product Discovery Views - Collapsible Section */}
          <div style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'visible' }}>
            <button
              type="button"
              onClick={() => toggleSection('jiraProductDiscovery')}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                border: 'none',
                borderBottom: expandedSections.jiraProductDiscovery ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#333',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efefef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                💡 JIRA Product Discovery (optional)
              </span>
              <span style={{ fontSize: '1.2rem', transform: expandedSections.jiraProductDiscovery ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </button>
            
            {expandedSections.jiraProductDiscovery && (
              <div style={{ padding: '1rem', backgroundColor: '#fff' }}>
                <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '1rem' }}>
                  Select an idea from JIRA Product Discovery (PM project)
                </p>
                
                {loadingJiraViews && (
                  <p style={{ fontSize: '0.8rem', color: '#0052cc', marginTop: '0.5rem' }}>
                    🔄 Loading ideas...
                  </p>
                )}

                {!loadingJiraViews && jiraProductDiscoveryViews.length === 0 && (
                  <p style={{ fontSize: '0.8rem', color: '#999' }}>
                    No ideas found in PM project
                  </p>
                )}

                {!loadingJiraViews && jiraProductDiscoveryViews.length > 0 && (
                  <>
                    {/* Filter Input */}
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>
                      � Filter Ideas:
                    </label>
                    
                    <input
                      type="text"
                      placeholder="Search by name or key..."
                      value={jiraIdeasFilter}
                      onChange={(e) => setJiraIdeasFilter(e.target.value)}
                      style={{
                        ...styles.input,
                        width: '100%',
                        boxSizing: 'border-box',
                        marginBottom: '0.5rem',
                        padding: '0.5rem',
                        fontSize: '0.9rem'
                      }}
                    />
                    
                    {/* Ideas Dropdown */}
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>
                      💡 Select an Idea:
                    </label>

                    {(() => {
                      const filteredIdeas = jiraProductDiscoveryViews.filter(idea =>
                        (idea.key || '').toLowerCase().includes(jiraIdeasFilter.toLowerCase()) ||
                        (idea.name || idea.summary || '').toLowerCase().includes(jiraIdeasFilter.toLowerCase())
                      );
                      
                      return (
                        <>
                          <select
                            value={selectedJiraIdea?.id || ''}
                            onChange={(e) => {
                              const idea = jiraProductDiscoveryViews.find(i => i.id === e.target.value);
                              setSelectedJiraIdea(idea);
                              
                              // Auto-populate issue type to EPIC if not already selected
                              if (idea) {
                                const currentIssueTypeName = issueTypes.find(t => t.id === issueType)?.name.toLowerCase() || '';
                                if (currentIssueTypeName !== 'epic') {
                                  // Find the Epic issue type ID and set it
                                  const epicType = issueTypes.find(type => type.name.toLowerCase() === 'epic');
                                  if (epicType) {
                                    setIssueType(epicType.id);
                                  }
                                }
                              }
                              
                              // Auto-populate summary and description from idea
                              if (idea) {
                                setStoryName(idea.summary || idea.name || '');
                                // Set description from idea's description field - convert ADF to HTML if needed
                                let descriptionText = idea.description || '';
                                
                                // If description is an object (ADF format), convert to HTML
                                if (typeof descriptionText === 'object') {
                                  descriptionText = convertAdfToHtml(descriptionText);
                                } else if (typeof descriptionText !== 'string') {
                                  // Fallback: if it's something else, convert to string
                                  descriptionText = String(descriptionText);
                                }
                                
                                setDescription(descriptionText);
                                console.log('📋 Auto-populated Epic from idea:', {
                                  summary: idea.summary || idea.name,
                                  description: descriptionText,
                                  issueTypeId: issueTypes.find(type => type.name.toLowerCase() === 'epic')?.id
                                });
                              }
                            }}
                            style={{
                              ...styles.input,
                              width: '100%',
                              boxSizing: 'border-box',
                              marginBottom: '0.5rem'
                            }}
                          >
                            <option value="">-- Select an Idea ({filteredIdeas.length} of {jiraProductDiscoveryViews.length} available) --</option>
                            {filteredIdeas.map(idea => (
                              <option key={idea.id} value={idea.id}>
                                {(idea.key || 'UNKNOWN')}: {idea.name || idea.summary || '(no name)'}
                              </option>
                            ))}
                          </select>

                          {filteredIdeas.length === 0 && jiraIdeasFilter && (
                            <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.5rem' }}>
                              No ideas match your filter
                            </p>
                          )}
                        </>
                      );
                    })()}

                    {selectedJiraIdea && (
                      <>
                        <div style={{ 
                          marginTop: '0.5rem', 
                          padding: '0.5rem', 
                          backgroundColor: '#f0f8ff', 
                          borderRadius: '4px',
                          fontSize: '0.8rem'
                        }}>
                          <p><strong>Selected:</strong> {selectedJiraIdea.key || 'UNKNOWN'} - {selectedJiraIdea.name || selectedJiraIdea.summary || '(no name)'}</p>
                          <p><strong>Status:</strong> {selectedJiraIdea.status || 'Unknown'}</p>
                          {selectedJiraIdea.priority && <p><strong>Priority:</strong> {selectedJiraIdea.priority}</p>}
                        </div>

                        {/* Reload Button - Refresh Summary and Description */}
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedJiraIdea) {
                              // Update summary
                              setStoryName(selectedJiraIdea.summary || selectedJiraIdea.name || '');
                              
                              // Update description with ADF conversion if needed
                              let descriptionText = selectedJiraIdea.description || '';
                              if (typeof descriptionText === 'object') {
                                descriptionText = convertAdfToHtml(descriptionText);
                              } else if (typeof descriptionText !== 'string') {
                                descriptionText = String(descriptionText);
                              }
                              setDescription(descriptionText);
                              
                              console.log('🔄 Reloaded idea data into EPIC form:', {
                                summary: selectedJiraIdea.summary || selectedJiraIdea.name,
                                description: descriptionText
                              });
                            }
                          }}
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            width: '100%',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#4CAF50'}
                        >
                          🔄 Reload Data into Epic Form
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Slack Channel Input - Collapsible Section */}
          <div style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'visible' }}>
            <button
              type="button"
              onClick={() => toggleSection('slack')}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                border: 'none',
                borderBottom: expandedSections.slack ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#333',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efefef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                💬 Slack Channel (optional)
              </span>
              <span style={{ fontSize: '1.2rem', transform: expandedSections.slack ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </button>
            
            {expandedSections.slack && (
              <div style={{ padding: '1rem', backgroundColor: '#fff' }}>
                <input
                  id="slack-channel"
                  type="text"
                  value={slackChannel}
                  onChange={(e) => setSlackChannel(e.target.value)}
                  placeholder="#channel-name or @username"
                  disabled={!isFormEnabled || isReviewing}
                  style={{
                    ...styles.input,
                    width: '100%',
                    boxSizing: 'border-box',
                    opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                    backgroundColor: (!isFormEnabled || isReviewing) ? '#f0f0f0' : '#fff'
                  }}
                />
                <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                  Enter a Slack channel (e.g., #project-updates) or user (e.g., @john.doe) for notifications
                </p>
              </div>
            )}
          </div>

          {/* GitHub Repository and Branch Selection - Collapsible Section */}
          <div style={{ marginBottom: '1.5rem', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'visible' }}>
            <button
              type="button"
              onClick={() => toggleSection('github')}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                border: 'none',
                borderBottom: expandedSections.github ? '1px solid #e0e0e0' : 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#333',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efefef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🌿 Input from Repository (optional)
              </span>
              <span style={{ fontSize: '1.2rem', transform: expandedSections.github ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </button>
            
            {expandedSections.github && (
              <div style={{ padding: '1rem', backgroundColor: '#fff' }} ref={repoDropdownRef}>
                <div style={{ position: 'relative', marginBottom: '1rem', display: 'block' }}>
                  <label htmlFor="github-repo-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                    Repository
                  </label>
                  <input
                    id="github-repo-filter"
                    type="text"
                    placeholder={loadingRepos ? '⏳ Loading repositories...' : '🔍 Search repositories...'}
                    value={repoFilter}
                    onChange={(e) => {
                      setRepoFilter(e.target.value);
                      setShowRepoList(true);
                    }}
                    onFocus={() => setShowRepoList(true)}
                    disabled={!isFormEnabled || isReviewing || loadingRepos}
                    style={{
                      ...styles.input,
                      width: '100%',
                      boxSizing: 'border-box',
                      opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                      backgroundColor: (!isFormEnabled || isReviewing) ? '#f0f0f0' : '#fff'
                    }}
                  />
                  
                  {/* Filtered Repository Dropdown */}
                  {showRepoList && !loadingRepos && repositories.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                      zIndex: 10000
                    }}>
                      {repositories
                        .filter((repo) =>
                          repo.fullName.toLowerCase().includes(repoFilter.toLowerCase())
                        )
                        .map((repo) => (
                          <div
                            key={repo.id}
                            onClick={() => {
                              setSelectedRepo(repo.fullName);
                              setRepoFilter(repo.fullName);
                              setShowRepoList(false);
                              setGithubBranch(''); // Reset branch when repo changes
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              borderBottom: '1px solid #eee',
                              cursor: 'pointer',
                              backgroundColor: selectedRepo === repo.fullName ? '#e3f2fd' : '#fff',
                              transition: 'background-color 0.2s',
                              ':hover': { backgroundColor: '#f5f5f5' }
                            }}
                            onMouseEnter={(e) => {
                              if (selectedRepo !== repo.fullName) {
                                e.target.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedRepo === repo.fullName) {
                                e.target.style.backgroundColor = '#e3f2fd';
                              } else {
                                e.target.style.backgroundColor = '#fff';
                              }
                            }}
                          >
                            <span style={{ fontWeight: selectedRepo === repo.fullName ? 'bold' : 'normal' }}>
                              {repo.fullName}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '0.8rem', color: '#999', marginBottom: '1rem' }}>
                  {selectedRepo ? `Selected: ${selectedRepo}` : 'Search and select a GitHub repository'}
                </p>
                {/* GitHub Branch Selection */}
                {selectedRepo && (
                  <div style={{ marginBottom: '0', position: 'relative' }} ref={branchDropdownRef}>
                    <label htmlFor="github-branch" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>
                      Branch
                    </label>
                    <input
                      id="github-branch"
                      type="text"
                      placeholder={loadingBranches ? '⏳ Loading branches...' : '🌿 Search branches...'}
                      value={branchFilter}
                      onChange={(e) => {
                        setBranchFilter(e.target.value);
                        setShowBranchList(true);
                      }}
                      onFocus={() => setShowBranchList(true)}
                      disabled={!isFormEnabled || isReviewing || loadingBranches || branches.length === 0}
                      style={{
                        ...styles.input,
                        width: '100%',
                        boxSizing: 'border-box',
                        opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                        backgroundColor: (!isFormEnabled || isReviewing) ? '#f0f0f0' : '#fff'
                      }}
                    />
                    
                    {/* Filtered Branch Dropdown */}
                    {showBranchList && !loadingBranches && branches.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '250px',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                        zIndex: 10000
                      }}>
                        {branches
                          .filter((branch) =>
                            branch.name.toLowerCase().includes(branchFilter.toLowerCase())
                          )
                          .map((branch) => (
                            <div
                              key={branch.name}
                              onClick={() => {
                                setGithubBranch(branch.name);
                                setBranchFilter(branch.name);
                                setShowBranchList(false);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                borderBottom: '1px solid #eee',
                                cursor: 'pointer',
                                backgroundColor: githubBranch === branch.name ? '#e3f2fd' : '#fff',
                                transition: 'background-color 0.2s',
                                ':hover': { backgroundColor: '#f5f5f5' }
                              }}
                              onMouseEnter={(e) => {
                                if (githubBranch !== branch.name) {
                                  e.target.style.backgroundColor = '#f5f5f5';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (githubBranch === branch.name) {
                                  e.target.style.backgroundColor = '#e3f2fd';
                                } else {
                                  e.target.style.backgroundColor = '#fff';
                                }
                              }}
                            >
                              <span style={{ fontWeight: githubBranch === branch.name ? 'bold' : 'normal' }}>
                                {branch.name} {branch.protected ? '🔒' : ''}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                    
                    <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
                      {branches.length === 0 ? 'No branches available' : `${branches.length} branch${branches.length !== 1 ? 'es' : ''} available`}
                      {githubBranch && ` • Selected: ${githubBranch}`}
                    </p>
                  </div>
                )}

                {/* GitHub URL Paste Section */}
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                  <button
                    type="button"
                    onClick={() => setShowBranchUrlInput(!showBranchUrlInput)}
                    style={{
                      backgroundColor: '#f8f9fa',
                      color: '#007bff',
                      border: '1px solid #007bff',
                      borderRadius: '4px',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {showBranchUrlInput ? '✕ Hide URL Paste' : '+ Paste GitHub URL'}
                  </button>

                  {showBranchUrlInput && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500', color: '#333' }}>
                        Paste GitHub Branch URL:
                      </label>
                      <input
                        type="text"
                        placeholder="Paste: https://github.com/owner/repo/tree/branch-name"
                        value={githubBranchUrl}
                        onChange={handleGithubBranchUrlPaste}
                        style={{
                          ...styles.input,
                          width: '100%',
                          boxSizing: 'border-box'
                        }}
                      />
                      <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                        Supports: https://github.com/owner/repo/tree/branch-name or .../blob/branch-name/file
                      </p>
                    </div>
                  )}

                  {/* Tree Structure Display */}
                  {treeStructure.length > 0 && (
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                      <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#333', fontSize: '0.95rem' }}>
                        📁 Repository Structure (Branch: {githubBranch})
                      </label>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {treeStructure.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '0.5rem',
                              marginBottom: '0.25rem',
                              backgroundColor: selectedTreeItem?.path === item.path ? '#e3f2fd' : '#fff',
                              border: '1px solid #e0e0e0',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onClick={() => {
                              setSelectedTreeItem(item);
                              if (item.type === 'dir') {
                                toggleTreeNode(item.path);
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (selectedTreeItem?.path !== item.path) {
                                e.target.style.backgroundColor = '#f5f5f5';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedTreeItem?.path !== item.path) {
                                e.target.style.backgroundColor = '#fff';
                              }
                            }}
                          >
                            <span style={{ fontSize: '1rem' }}>
                              {item.type === 'dir' ? (expandedTreeNodes[item.path] ? '📂' : '📁') : '📄'}
                            </span>
                            <span style={{
                              flex: 1,
                              fontWeight: selectedTreeItem?.path === item.path ? '600' : '400',
                              fontSize: '0.9rem',
                              color: selectedTreeItem?.path === item.path ? '#007bff' : '#333'
                            }}>
                              {item.name}
                            </span>
                            {item.type === 'file' && (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>
                                📄 File
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {selectedTreeItem && (
                        <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#e3f2fd', borderRadius: '3px', fontSize: '0.85rem' }}>
                          <strong>Selected:</strong> {selectedTreeItem.name} ({selectedTreeItem.type})
                          <br />
                          <strong>Path:</strong> {selectedTreeItem.path}
                          <br />
                          <a
                            href={selectedTreeItem.html_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#007bff', textDecoration: 'none', marginTop: '0.5rem', display: 'inline-block' }}
                          >
                            View on GitHub →
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {loadingTreeStructure && (
                    <div style={{ marginTop: '1rem', padding: '1rem', textAlign: 'center', color: '#666' }}>
                      🔄 Loading repository structure...
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
            </div>
          )}
        </div>

        {/* Only show Issue Type in Add mode or when an issue is selected in Edit mode */}
        {(mode === 'add' || selectedIssue) && (
          <div style={styles.formGroup}>
            <label htmlFor="issue-type" style={styles.label}>Issue Type:</label>
            <select
              id="issue-type"
              value={issueType}
              onChange={(e) => {
                setIssueType(e.target.value);
                // Auto-sync Rules Template to match the Issue Type
                const selectedTypeObj = issueTypes.find(t => t.id === e.target.value);
                const selectedTypeName = selectedTypeObj?.name || 'Story';
                // Update template to match issue type if available
                if (availableTemplates.some(t => t.toLowerCase() === selectedTypeName.toLowerCase())) {
                  setSelectedTemplate(selectedTypeName);
                  setTemplateManuallySet(false); // Reset manual flag to auto-mode
                  console.log(`🔄 [ISSUE-TYPE-CHANGE] Auto-synced Rules Template to: ${selectedTypeName}`);
                } else {
                  // If no exact match, default to Story
                  setSelectedTemplate('Story');
                  setTemplateManuallySet(false);
                  console.log(`🔄 [ISSUE-TYPE-CHANGE] No matching template for ${selectedTypeName}, defaulting to Story`);
                }
              }}
              style={styles.select}
              disabled={!isFormEnabled || isReviewing || issueTypes.length === 0}
            >
              {issueTypes.length === 0 ? (
                <option value="">Loading issue types...</option>
              ) : (
                issueTypes
                  .sort((a, b) => {
                    // Epic always comes first
                    if (a.name.toLowerCase() === 'epic') return -1;
                    if (b.name.toLowerCase() === 'epic') return 1;
                    // Then alphabetically
                    return a.name.localeCompare(b.name);
                  })
                  .map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))
              )}
            </select>
          </div>
        )}

        {/* Only show Epic selection if the issue type is NOT Epic and in Add mode or issue selected in Edit mode */}
        {(mode === 'add' || selectedIssue) && issueTypes.find(t => t.id === issueType)?.name.toLowerCase() !== 'epic' && (
          <div style={styles.formGroup}>
            <label htmlFor="epic-selector" style={styles.label}>Epic (Optional):</label>
            <select
              id="epic-selector"
              value={epicKey}
              onChange={(e) => setEpicKey(e.target.value)}
              style={styles.select}
              disabled={loadingEpics || !isFormEnabled || isReviewing}
            >
              <option value="">{loadingEpics ? 'Loading Epics...' : '-- Select an Epic --'}</option>
              {epics.map((epic) => (
                <option key={epic.key} value={epic.key}>
                  {epic.key}: {epic.summary}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Separation line */}
        <div style={{ borderBottom: '2px solid #dee2e6', margin: '0rem 0' }}></div>

        {/* Template Selector - Select rules before review (hidden when custom rules loaded) */}
        {availableTemplates.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Show dropdown only when no custom rules loaded */}
            {!customRulesFileName && (
              <>
                <label htmlFor="template-selector" style={{ fontWeight: 'bold', color: '#333' }}>
                  Rules Template:
                </label>
                <span style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic' }}>
                  (State: {selectedTemplate || 'none'}{templateManuallySet ? ' [manual]' : ' [auto]'})
                </span>
                <select
                  id="template-selector"
                  value={selectedTemplate || issueTypes.find(t => t.id === issueType)?.name || 'Story'}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('🔧 [TEMPLATE-CHANGE] User selected template:', newValue);
                    console.log('🔧 [TEMPLATE-CHANGE] Previous selectedTemplate:', selectedTemplate);
                    setSelectedTemplate(newValue);
                    setTemplateManuallySet(true); // Mark that user manually changed the template
                    console.log('🔧 [TEMPLATE-CHANGE] Updated selectedTemplate to:', newValue);
                  }}
                  disabled={!isFormEnabled || isReviewing}
                  style={{
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    backgroundColor: '#fff',
                    cursor: (!isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                    opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1
                  }}
                >
                  {availableTemplates.map(template => (
                    <option key={template} value={template}>
                      {template}
                    </option>
                  ))}
                </select>
              </>
            )}
            {/* Custom rules buttons - always visible */}
            <button
              type="button"
              onClick={() => customRulesInputRef.current?.click()}
              disabled={!isFormEnabled || isReviewing}
              style={{
                padding: '6px 12px',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: (!isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
              title="Load custom rules from a JSON file"
            >
              📁 Load Custom Rules
            </button>
            <input
              ref={customRulesInputRef}
              type="file"
              accept=".json"
              onChange={handleLoadCustomRules}
              style={{ display: 'none' }}
            />
            {customRulesFileName && (
              <button
                type="button"
                onClick={handleClearCustomRules}
                style={{
                  padding: '4px 8px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap'
                }}
                title="Clear custom rules"
              >
                ✕ Clear
              </button>
            )}
            {customRulesFileName && (
              <button
                type="button"
                onClick={handleReloadCustomRules}
                style={{
                  padding: '4px 8px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#0052cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  marginLeft: '4px'
                }}
                title="Re-read the custom rules file from disk"
              >
                🔄 Reload
              </button>
            )}
            {customRulesFileName && (
              <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                Custom: {customRulesFileName.substring(0, 20)}
                {customRulesFileName.length > 20 ? '...' : ''}
              </span>
            )}
            <button
              type="button"
              onClick={() => handleReviewDescription()}
              disabled={!description || !isFormEnabled || isReviewing}
              style={{
                ...styles.generateDescButton,
                backgroundColor: isReviewing ? '#6c757d' : '#17a2b8',
                opacity: (!description || !isFormEnabled || isReviewing) ? 0.6 : 1,
                cursor: (!description || !isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                animation: isReviewing ? 'spin 1s linear infinite' : 'none'
              }}
              title={isReviewing ? 'Reviewing description...' : 'Review description for ticket readiness'}
            >
              {isReviewing ? '⏳' : '🔍'}
            </button>
            <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
              (Select validation rules before clicking 🔍 Review)
            </span>
            {(() => {
              const currentIssueTypeName = issueTypes.find(t => t.id === issueType)?.name;
              const usingFallback = currentIssueTypeName && 
                                   selectedTemplate === 'Story' && 
                                   currentIssueTypeName !== 'Story' &&
                                   !templateManuallySet &&
                                   !availableTemplates.some(t => t.toLowerCase() === currentIssueTypeName.toLowerCase());
              return usingFallback ? (
                <span style={{ fontSize: '0.8rem', color: '#856404', backgroundColor: '#fff3cd', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>
                  ℹ️ Using Story rules (no {currentIssueTypeName} template)
                </span>
              ) : null;
            })()}
          </div>
        )}

        {/* Spacing below Rules Template */}
        <div style={{ marginBottom: '0.5rem' }}></div>

        <div style={styles.formGroup}>
          <label htmlFor="issue-name" style={styles.label}>
            {mode === 'edit' && !selectedIssue 
              ? 'Issue Summary:' 
              : `${issueTypes.find(t => t.id === issueType)?.name || 'Issue'} Summary:`
            }
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              id="issue-name"
              type="text"
              value={storyName}
              onChange={(e) => setStoryName(e.target.value)}
              required
              style={{...styles.input, flex: 1}}
              disabled={!isFormEnabled || isReviewing}
              placeholder={mode === 'edit' && !selectedIssue 
                ? 'Select an issue first' 
                : `Enter ${issueTypes.find(t => t.id === issueType)?.name?.toLowerCase() || 'issue'} title`
              }
            />
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={!storyName || !isFormEnabled || isReviewing}
              style={{
                ...styles.generateDescButton,
                opacity: (!storyName || !isFormEnabled || isReviewing) ? 0.6 : 1,
                cursor: (!storyName || !isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer'
              }}
              title={
                isGeneratingDescription 
                  ? 'Generating description...' 
                  : 'Generate description from title'
              }
            >
              ✨
            </button>
            <LyricAIPopup 
              onInsert={(text) => setStoryName(text)} 
              selectedModel={selectedModel}
              disabled={isReviewing}
              currentValue={storyName}
              label="Get Issue Name"
            />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="issue-description" style={styles.label}>
            Description:
            {isGeneratingDescription && <span style={{ fontSize: '0.9em', color: '#ff9800', fontWeight: 'bold' }}> 🔄 Generating with AI...</span>}
            {isReviewing && <span style={{ fontSize: '0.9em', color: '#17a2b8' }}> (Reviewing...)</span>}
          </label>
          
          {/* WARN: Display embedded Lyric Review from loaded JIRA ticket in RED */}
          {embeddedReviewFromJira && (
            <div style={{
              backgroundColor: '#ffebee',
              border: '3px solid #d32f2f',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: '#d32f2f',
              fontWeight: 'bold'
            }}>
              ⚠️ <strong>EMBEDDED LYRIC REVIEW DETECTED - THIS WAS STRIPPED FROM DESCRIPTION</strong>
              <div style={{ marginTop: '8px', color: '#c62828' }}>
                {embeddedReviewFromJira}
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '1rem' }}>
            <div style={{ flex: 1 }}>
              <RichTextEditor
                ref={richTextEditorRef}
                value={description}
                onChange={setDescription}
                placeholder="Provide a detailed description"
                disabled={!isFormEnabled || isReviewing}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <LyricAIPopup 
                onInsert={(text) => {
                  // Check if there's a selection in the RichTextEditor
                  if (richTextEditorRef.current && richTextEditorRef.current.hasSelection && richTextEditorRef.current.hasSelection()) {
                    // Replace selected text
                    setDescriptionHistory(description);
                    if (richTextEditorRef.current.replaceSelectedText) {
                      richTextEditorRef.current.replaceSelectedText(text);
                    }
                  } else {
                    // No selection, append to description
                    setDescriptionHistory(description);
                    if (typeof description === 'string' && description.trim()) {
                      setDescription(text + '<br/><br/>' + description);
                    } else {
                      setDescription(text);
                    }
                  }
                }} 
                selectedModel={selectedModel}
                label="Get Description Help."
                style={{ marginTop: '0px', marginLeft: '0px' }}
                currentValue={description}
                getSelectedText={() => {
                  // Get selected text directly from the RichTextEditor
                  if (richTextEditorRef.current && richTextEditorRef.current.getSelectedText) {
                    return richTextEditorRef.current.getSelectedText();
                  }
                  return '';
                }}
                disabled={isReviewing}
              />
              {descriptionHistory && descriptionHistory !== description && (
                <button
                  type="button"
                  onClick={() => {
                    setDescription(descriptionHistory);
                    setDescriptionHistory('');
                  }}
                  disabled={isReviewing}
                  style={{
                    width: '30px',
                    height: '30px',
                    padding: '0',
                    backgroundColor: '#ff9800',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isReviewing ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isReviewing ? 0.6 : 1
                  }}
                  title="Undo last AI paste"
                >
                  ↶
                </button>
              )}
              {typeof description === 'string' && description.trim() && (
                <button
                  type="button"
                  onClick={() => setShowClearDescriptionConfirm(true)}
                  disabled={isReviewing}
                  style={{
                    width: '30px',
                    height: '30px',
                    padding: '0',
                    backgroundColor: '#fff',
                    color: '#666',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: isReviewing ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isReviewing ? 0.6 : 1
                  }}
                  title="Clear entire description"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>

          {/* Estimation: only render in edit mode when at least one of the
              JIRA estimation fields is configured on this instance. */}
          {mode === 'edit' && (storyPointsFieldId || effortDaysFieldId) && (
            <div style={{
              marginTop: '1rem',
              marginBottom: '1rem',
              padding: '16px',
              backgroundColor: '#f3f7fb',
              border: '1px solid #c9d6e2',
              borderRadius: '6px',
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#0052cc' }}>📏 Estimation</h4>

              {/* Admin notice: warn when one or more fields exist globally but
                  aren't on this project's edit screen. The JIRA PUT would fail
                  with "not on appropriate screen" — we tell the user why and
                  what action a JIRA admin needs to take, BEFORE they try. */}
              {((effortDaysFieldId && !effortDaysEditable) || (storyPointsFieldId && !storyPointsEditable)) && (() => {
                const blocked = [
                  effortDaysFieldId && !effortDaysEditable ? 'Effort (Days)' : null,
                  storyPointsFieldId && !storyPointsEditable ? 'Story Points' : null,
                ].filter(Boolean);
                const projectKey = (selectedIssue?.key || '').split('-')[0];
                return (
                  <div style={{
                    padding: '8px 12px',
                    marginBottom: '12px',
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#665100',
                  }}>
                    <strong>⚠️ JIRA admin action needed for project {projectKey}:</strong> the {blocked.join(' and ')} field{blocked.length > 1 ? 's are' : ' is'} not on this project's <em>Edit</em> screen, so {blocked.length > 1 ? 'their' : 'its'} value can't be saved. Ask an admin to go to <em>Project settings → Screens → Edit screen</em> and add the missing field{blocked.length > 1 ? 's' : ''}. You can still see the current value{blocked.length > 1 ? 's' : ''} below, but editing is disabled.
                  </div>
                );
              })()}

              {/* Existing values, side-by-side */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                {effortDaysFieldId && (
                  <div style={{ flex: 1 }}>
                    <label style={styles.label} htmlFor="effort-days">
                      Effort (Days){!effortDaysEditable && <span style={{ color: '#999', fontWeight: 400 }}> (read-only — not on project screen)</span>}
                    </label>
                    <input
                      id="effort-days"
                      type="number"
                      min="0"
                      step="0.25"
                      value={effortDays}
                      onChange={(e) => setEffortDays(e.target.value)}
                      placeholder="e.g. 2.5"
                      style={{ ...styles.input, width: '100%', boxSizing: 'border-box', backgroundColor: !effortDaysEditable ? '#f5f5f5' : undefined }}
                      disabled={!isFormEnabled || isReviewing || estimating || !effortDaysEditable}
                      title={!effortDaysEditable ? 'Effort (Days) is not on this project\'s edit screen. Ask a JIRA admin to add it.' : ''}
                    />
                  </div>
                )}
                {storyPointsFieldId && (
                  <div style={{ flex: 1 }}>
                    <label style={styles.label} htmlFor="story-points">
                      Story Points{!storyPointsEditable && <span style={{ color: '#999', fontWeight: 400 }}> (read-only — not on project screen)</span>}
                    </label>
                    <input
                      id="story-points"
                      type="number"
                      min="0"
                      step="1"
                      value={storyPoints}
                      onChange={(e) => setStoryPoints(e.target.value)}
                      placeholder="e.g. 5"
                      style={{ ...styles.input, width: '100%', boxSizing: 'border-box', backgroundColor: !storyPointsEditable ? '#f5f5f5' : undefined }}
                      disabled={!isFormEnabled || isReviewing || estimating || !storyPointsEditable}
                      title={!storyPointsEditable ? 'Story Points is not on this project\'s edit screen. Ask a JIRA admin to add it.' : ''}
                    />
                  </div>
                )}
              </div>

              {/* AI estimate widget */}
              <div style={{
                paddingTop: '12px',
                borderTop: '1px dashed #c9d6e2',
              }}>
                <div style={{ fontSize: '0.9rem', color: '#444', marginBottom: '8px', fontWeight: 600 }}>
                  Analyze description and estimate
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ ...styles.label, fontSize: '0.85rem' }} htmlFor="estimate-devs">Developers</label>
                    <select
                      id="estimate-devs"
                      value={estimateDevCount}
                      onChange={(e) => setEstimateDevCount(Number(e.target.value))}
                      style={{ ...styles.input, padding: '0.5rem' }}
                      disabled={!isFormEnabled || estimating}
                    >
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ ...styles.label, fontSize: '0.85rem' }}>Using AI</label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={estimateUsingAi}
                        onChange={(e) => setEstimateUsingAi(e.target.checked)}
                        disabled={!isFormEnabled || estimating}
                      />
                      <span>{estimateUsingAi ? 'Yes' : 'No'}</span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={runEstimate}
                    disabled={!isFormEnabled || estimating || !description || !storyName}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: estimating ? '#999' : '#0052cc',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: (!isFormEnabled || estimating || !description || !storyName) ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                    }}
                    title={!storyName ? 'Summary required' : !description ? 'Description required' : 'Run AI estimate'}
                  >
                    {estimating ? 'Estimating…' : 'Go'}
                  </button>
                </div>
                {estimateError && (
                  <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '8px' }}>
                    ⚠️ {estimateError}
                  </div>
                )}
                {estimateRationale && !estimateError && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#fff',
                    border: '1px solid #c9d6e2',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#333',
                    whiteSpace: 'pre-wrap',
                  }}>
                    <strong>AI rationale:</strong> {estimateRationale}
                  </div>
                )}
                {/* Capacity callout — shows the estimate as a % of weekly
                    team capacity so the user can sanity-check before saving.
                    Color-codes by impact: green <50%, amber 50-100%, red >100%. */}
                {estimateCapacity && (() => {
                  const pct = Math.round((estimateCapacity.percentOfWeek || 0) * 100);
                  const color = pct >= 100 ? '#c62828' : pct >= 50 ? '#ef6c00' : '#2e7d32';
                  const bg    = pct >= 100 ? '#ffebee' : pct >= 50 ? '#fff3e0' : '#e8f5e9';
                  return (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: bg,
                      border: `1px solid ${color}`,
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      color,
                    }}>
                      <strong>≈ {pct}% of {estimateCapacity.devCount}-dev weekly capacity</strong>
                      {' '}({storyPoints} SP / {estimateCapacity.storyPointsAvailable} SP available)
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {reviewResult && (
            <div style={{
              marginTop: '8px',
              padding: '16px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              color: '#000',
              position: 'relative'
            }}>
              <button
                type="button"
                onClick={() => setReviewResult(null)}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close review"
              >
                ✕
              </button>
              <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Review Result</h4>
              
              {/* Email PM Button */}
              <div style={{ marginBottom: '16px' }}>
                <button
                  type="button"
                  onClick={handleEmailPM}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#0066cc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0052a3'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0066cc'}
                  title="Generate email to send to Product Manager"
                >
                  📧 Email PM
                </button>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  Acceptable Jira Ticket: {' '}
                  <span style={{ 
                    color: reviewResult.status === 'approved' ? '#28a745' : '#dc3545',
                    fontWeight: 'bold'
                  }}>
                    {reviewResult.status === 'approved' ? 'YES' : 'NO'}
                  </span>
                </span>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <strong>JSON Template Used:</strong> {String(reviewResult.rulesApplied)}.json
              </div>

              <div style={{ marginBottom: '16px' }}>
                <strong>AI Model Used:</strong> {reviewResult.modelUsed || 'Not specified'}
              </div>

              {/* Display the AI message/feedback */}
              {reviewResult.message && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: reviewResult.status === 'approved' ? '#d4edda' : '#fff3cd',
                  border: `1px solid ${reviewResult.status === 'approved' ? '#c3e6cb' : '#ffeaa7'}`,
                  borderRadius: '4px'
                }}>
                  <h5 style={{ 
                    marginTop: 0, 
                    marginBottom: '12px',
                    color: reviewResult.status === 'approved' ? '#155724' : '#856404',
                    fontSize: '1rem'
                  }}>
                    {reviewResult.status === 'approved' ? '✅ Feedback' : '💬 AI Feedback'}
                  </h5>
                  <div style={{ 
                    color: reviewResult.status === 'approved' ? '#155724' : '#856404',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6'
                  }}>
                    {reviewResult.message}
                  </div>
                </div>
              )}

              {/* Show message if no details provided but ticket was rejected */}
              {reviewResult.status === 'needs_clarification' && 
               (!reviewResult.issues || reviewResult.issues.length === 0) && 
               (!reviewResult.recommendations || reviewResult.recommendations.length === 0) && 
               !reviewResult.parseError && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px'
                }}>
                  <p style={{ 
                    margin: 0,
                    color: '#856404',
                    fontSize: '0.95rem',
                    lineHeight: '1.5'
                  }}>
                    ⚠️ The AI determined this ticket needs clarification, but didn't provide specific details. 
                    This may indicate a communication issue with the AI. Please review your ticket manually 
                    against the validation rules below, or try the review again.
                  </p>
                </div>
              )}

              {/* Parse Error Debug Info */}
              {reviewResult.parseError && reviewResult.rawResponse && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: '4px'
                }}>
                  <h5 style={{ 
                    marginTop: 0, 
                    marginBottom: '12px',
                    color: '#721c24',
                    fontSize: '1rem'
                  }}>
                    ⚠️ AI Response Format Issue
                  </h5>
                  <p style={{ margin: '8px 0', color: '#721c24', fontSize: '0.9rem' }}>
                    The AI returned a text response instead of structured data. Here's what it said:
                  </p>
                  <details style={{ marginTop: '12px' }}>
                    <summary style={{ 
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      color: '#721c24',
                      marginBottom: '8px'
                    }}>
                      View Raw AI Response
                    </summary>
                    <pre style={{ 
                      backgroundColor: '#fff',
                      padding: '12px',
                      borderRadius: '4px',
                      overflow: 'auto',
                      fontSize: '0.85rem',
                      color: '#333',
                      maxHeight: '300px',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}>
                      {reviewResult.rawResponse}
                    </pre>
                  </details>
                </div>
              )}

              {/* Issues Found */}
              {reviewResult.issues && reviewResult.issues.length > 0 && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <h5 style={{ 
                      margin: 0,
                      color: '#856404',
                      fontSize: '1rem'
                    }}>
                      ⚠️ Issues Found
                    </h5>
                    <button
                      type="button"
                      onClick={handleQuickFix}
                      disabled={isApplyingFix}
                      style={{
                        display: 'none',
                        padding: '6px 12px',
                        backgroundColor: isApplyingFix ? '#ccc' : '#ffc107',
                        color: isApplyingFix ? '#666' : '#000',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: isApplyingFix ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        transition: 'background-color 0.2s',
                        opacity: isApplyingFix ? 0.7 : 1
                      }}
                      onMouseOver={(e) => {
                        if (!isApplyingFix) e.target.style.backgroundColor = '#e0a800';
                      }}
                      onMouseOut={(e) => {
                        if (!isApplyingFix) e.target.style.backgroundColor = '#ffc107';
                      }}
                    >
                      {isApplyingFix ? (
                        <>
                          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⚙️</span>
                          {' '}Applying Fix...
                        </>
                      ) : (
                        <>🔧 Quick Fix</>
                      )}
                    </button>
                  </div>
                  <ul style={{ 
                    margin: 0,
                    paddingLeft: '20px',
                    color: '#856404'
                  }}>
                    {reviewResult.issues.map((issue, idx) => (
                      <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {reviewResult.recommendations && reviewResult.recommendations.length > 0 && (
                <div style={{ 
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '4px'
                }}>
                  <h5 style={{ 
                    marginTop: 0, 
                    marginBottom: '12px',
                    color: '#0c5460',
                    fontSize: '1rem'
                  }}>
                    💡 Recommendations
                  </h5>
                  <ul style={{ 
                    margin: 0,
                    paddingLeft: '20px',
                    color: '#0c5460'
                  }}>
                    {reviewResult.recommendations.map((rec, idx) => (
                      <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Enhanced Rule Details */}
              {reviewResult.ruleDetails && Object.keys(reviewResult.ruleDetails).length > 0 && (
                <div style={{ 
                  marginTop: '20px', 
                  paddingTop: '16px', 
                  borderTop: '2px solid #dee2e6' 
                }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', color: '#495057' }}>
                    Validation Rules Applied
                  </h5>
                  {Object.entries(reviewResult.ruleDetails).map(([ruleName, ruleData]) => (
                    <div key={ruleName} style={{ 
                      marginBottom: '16px',
                      padding: '12px',
                      backgroundColor: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px'
                    }}>
                      <h6 style={{ 
                        marginTop: 0, 
                        marginBottom: '8px',
                        color: '#007bff',
                        fontSize: '1rem'
                      }}>
                        {ruleData.reportHeading}
                        {ruleData.required && (
                          <span style={{ 
                            marginLeft: '8px',
                            padding: '2px 6px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            fontSize: '0.75rem',
                            borderRadius: '3px',
                            fontWeight: 'normal'
                          }}>
                            REQUIRED
                          </span>
                        )}
                      </h6>
                      
                      {ruleData.industryStandards && (
                        <div>
                          <p style={{ 
                            margin: '8px 0',
                            fontSize: '0.9rem',
                            color: '#495057'
                          }}>
                            <strong>Framework:</strong> {ruleData.industryStandards.framework}
                            {ruleData.industryStandards.format && (
                              <span> ({ruleData.industryStandards.format})</span>
                            )}
                          </p>
                          
                          {ruleData.industryStandards.description && (
                            <p style={{ 
                              margin: '8px 0',
                              fontSize: '0.85rem',
                              color: '#6c757d',
                              lineHeight: '1.5'
                            }}>
                              {ruleData.industryStandards.description}
                            </p>
                          )}
                          
                          {ruleData.industryStandards.bestPractices && ruleData.industryStandards.bestPractices.length > 0 && (
                            <details style={{ marginTop: '8px' }}>
                              <summary style={{ 
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '0.9rem',
                                color: '#495057',
                                marginBottom: '8px'
                              }}>
                                Best Practices ({ruleData.industryStandards.bestPractices.length})
                              </summary>
                              <ul style={{ 
                                margin: '8px 0',
                                paddingLeft: '20px',
                                fontSize: '0.85rem',
                                color: '#6c757d'
                              }}>
                                {ruleData.industryStandards.bestPractices.map((practice, idx) => (
                                  <li key={idx} style={{ marginBottom: '4px' }}>{practice}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                          
                          {ruleData.industryStandards.references && ruleData.industryStandards.references.length > 0 && (
                            <div style={{ 
                              marginTop: '8px',
                              fontSize: '0.8rem',
                              color: '#6c757d'
                            }}>
                              <strong>References:</strong> {ruleData.industryStandards.references.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Suggest Stories button for Epic type in Add mode */}
        {mode === 'add' && issueTypes.find(t => t.id === issueType)?.name.toLowerCase() === 'epic' && (
          <div style={{ marginBottom: '0.0rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', maxWidth: '50%' }}>
              <div style={{ flex: 1 }}>
                <button
                  type="button"
                  onClick={handleSuggestStories}
                  disabled={!storyName || !description || !isFormEnabled}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (!storyName || !description || !isFormEnabled) ? 'not-allowed' : 'pointer',
                    opacity: (!storyName || !description || !isFormEnabled) ? 0.6 : 1,
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    width: '100%',
                  }}
                >
                  {isSuggestingStories ? '🔄 Suggesting...' : '✨ Suggest Stories'}
                </button>
                <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                  AI will generate user stories
                </p>
              </div>
              <div style={{ flex: 1 }}>
                <button
                  type="button"
                  onClick={addNewStory}
                  disabled={!storyName || !description || !isFormEnabled}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#0052cc',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (!storyName || !description || !isFormEnabled) ? 'not-allowed' : 'pointer',
                    opacity: (!storyName || !description || !isFormEnabled) ? 0.6 : 1,
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    width: '100%',
                  }}
                  title="Add a new story manually"
                >
                  ➕ Add Story
                </button>
                <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                  Add story manually
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Suggested Stories Section */}
        {suggestedStories.length > 0 && mode === 'add' && (
          <div style={{ 
            marginBottom: '1.5rem', 
            padding: '1rem', 
            backgroundColor: '#f8f9fa', 
            border: '2px solid #28a745',
            borderRadius: '8px' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#28a745' }}>
                ✨ Suggested Stories ({suggestedStories.filter(s => s.include).length} selected)
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleSelectAllStories}
                  style={{
                    padding: '0.4rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#28a745',
                    border: '1px solid #28a745',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                    fontWeight: '500'
                  }}
                >
                  ✓ Select All
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAllStories}
                  style={{
                    padding: '0.4rem 0.75rem',
                    backgroundColor: '#fff',
                    color: '#666',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                    fontWeight: '500'
                  }}
                >
                  ✗ Deselect All
                </button>
              </div>
            </div>
            
            {suggestedStories.map((story, index) => (
              <div key={index} style={{ 
                marginBottom: '1rem', 
                padding: '1rem', 
                backgroundColor: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={story.include}
                    onChange={(e) => handleStoryInclusionChange(index, e.target.checked)}
                    style={{ marginRight: '0.75rem', cursor: 'pointer' }}
                  />
                  <h4 
                    onClick={() => handleStoryInclusionChange(index, !story.include)}
                    style={{ 
                      margin: 0, 
                      flex: 1, 
                      color: '#333',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontFamily: 'inherit'
                    }}
                  >
                    {story.summary || `Story ${index + 1}`}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleToggleStory(index)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      padding: '0',
                      marginRight: '0.5rem',
                      fontFamily: 'inherit',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {expandedStories[index] ? '▼' : '▶'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveStory(index)}
                    style={{
                      background: '#f8f9fa',
                      color: '#666',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '0',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontFamily: 'inherit',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    🗑️
                  </button>
                </div>

                {expandedStories[index] && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem', fontFamily: 'inherit' }}>
                        Story Title:
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <input
                          type="text"
                          value={story.summary}
                          onChange={(e) => handleStorySummaryChange(index, e.target.value)}
                          style={{ 
                            flex: 1,
                            padding: '0.5rem', 
                            border: '1px solid #ccc', 
                            borderRadius: '4px',
                            fontFamily: 'inherit',
                            fontSize: '1rem'
                          }}
                        />
                        <LyricAIPopup
                          fieldName={`Story ${index + 1} Title`}
                          currentValue={story.summary}
                          onInsert={(value) => handleStorySummaryChange(index, story.summary + '\n' + value)}
                          selectedModel={selectedModel}
                          context={`Epic: ${storyName}\nEpic Description: ${description}\n\nHelp with story title for this Epic.`}
                        />
                      </div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem', fontFamily: 'inherit' }}>
                        Description:
                        {story.isGenerating && <span style={{ fontSize: '0.9em', color: '#ff9800', fontWeight: 'bold', marginLeft: '0.5rem' }}>🔄 Generating with AI...</span>}
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <RichTextEditor
                            ref={(el) => {
                              if (el) storyDescriptionRefs.current[index] = el;
                            }}
                            value={story.description}
                            onChange={(value) => handleStoryDescriptionChange(index, value)}
                            placeholder="Enter story description"
                            disabled={false}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                          <LyricAIPopup
                            fieldName={`Story ${index + 1} Description`}
                            currentValue={story.description}
                            onInsert={(value) => {
                              // Check if there's a selection in the story RichTextEditor
                              const storyEditor = storyDescriptionRefs.current[index];
                              if (storyEditor && storyEditor.hasSelection && storyEditor.hasSelection()) {
                                // Replace selected text
                                if (storyEditor.replaceSelectedText) {
                                  storyEditor.replaceSelectedText(value);
                                }
                              } else {
                                // No selection, append to story description
                                handleStoryDescriptionChange(index, story.description + '<br/><br/>' + value);
                              }
                            }}
                            selectedModel={selectedModel}
                            context={`Epic: ${storyName}\nEpic Description: ${description}\nStory Title: ${story.summary}\n\nHelp with story description.`}
                            style={{ marginLeft: '0px' }}
                            getSelectedText={() => {
                              // Get selected text from the story RichTextEditor
                              const storyEditor = storyDescriptionRefs.current[index];
                              if (storyEditor && storyEditor.getSelectedText) {
                                return storyEditor.getSelectedText();
                              }
                              return '';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleGenerateStoryDescription(index)}
                            disabled={!story.summary || story.isGenerating}
                            style={{
                              padding: '0',
                              backgroundColor: '#28a745',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: (!story.summary || story.isGenerating) ? 'not-allowed' : 'pointer',
                              opacity: (!story.summary || story.isGenerating) ? 0.6 : 1,
                              fontSize: '1rem',
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title={story.isGenerating ? 'Generating description...' : 'Generate description from title'}
                          >
                            ✨
                          </button>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem', fontFamily: 'inherit' }}>
                        Acceptance Criteria:
                      </label>
                      {story.acceptanceCriteria && story.acceptanceCriteria.map((criteria, cIndex) => (
                        <div key={cIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'flex-start' }}>
                          <input
                            type="text"
                            value={criteria}
                            placeholder={`Acceptance criteria ${cIndex + 1}`}
                            onChange={(e) => handleStoryCriteriaChange(index, cIndex, e.target.value)}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              fontFamily: 'inherit',
                              fontSize: '0.95rem'
                            }}
                          />
                          <LyricAIPopup
                            fieldName={`Story ${index + 1} AC ${cIndex + 1}`}
                            currentValue={criteria}
                            onInsert={(value) => handleStoryCriteriaChange(index, cIndex, criteria + '\n' + value)}
                            selectedModel={selectedModel}
                            context={`Epic: ${storyName}\nStory: ${story.summary}\nDescription: ${story.description}\n\nHelp with acceptance criteria.`}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveCriteria(index, cIndex)}
                            title="Remove this criterion"
                            style={{
                              background: '#f8f9fa',
                              color: '#666',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '0',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontFamily: 'inherit',
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleAddCriteria(index)}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: '#f8f9fa',
                          color: '#007bff',
                          border: '1px solid #007bff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontFamily: 'inherit',
                          marginTop: '0.25rem'
                        }}
                      >
                        ➕ Add Criterion
                      </button>
                    </div>

                    {/* Story Review Section */}
                    <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem', fontFamily: 'inherit' }}>
                        Review Story:
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
                        {/* Load Custom Rules Button */}
                        <button
                          type="button"
                          onClick={() => customRulesInputRef.current?.click()}
                          disabled={!isFormEnabled || isReviewing}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (!isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            opacity: (!isFormEnabled || isReviewing) ? 0.6 : 1,
                            whiteSpace: 'nowrap'
                          }}
                          title="Load custom rules from a JSON file"
                        >
                          📁 Load Custom Rules
                        </button>

                        {/* Clear Button */}
                        {customRulesFileName && (
                          <button
                            type="button"
                            onClick={handleClearCustomRules}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              whiteSpace: 'nowrap'
                            }}
                            title="Clear custom rules"
                          >
                            ✕ Clear
                          </button>
                        )}

                        {/* Filename Display */}
                        {customRulesFileName && (
                          <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                            Custom: {customRulesFileName.substring(0, 20)}
                            {customRulesFileName.length > 20 ? '...' : ''}
                          </span>
                        )}

                        {/* Review Button */}
                        <button
                          type="button"
                          onClick={() => handleReviewStoryDescription(index)}
                          disabled={!story.summary || !story.description || !isFormEnabled || isReviewing}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: isReviewing ? '#6c757d' : '#17a2b8',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (!story.summary || !story.description || !isFormEnabled || isReviewing) ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            opacity: (!story.summary || !story.description || !isFormEnabled || isReviewing) ? 0.6 : 1,
                            whiteSpace: 'nowrap',
                            animation: isReviewing ? 'spin 1s linear infinite' : 'none'
                          }}
                          title={isReviewing ? 'Reviewing story...' : 'Review story description'}
                        >
                          {isReviewing ? '🔄 Reviewing...' : '🔍 Review'}
                        </button>
                      </div>

                      {/* Review Result Display */}
                      {story.reviewResult && (
                        <div style={{
                          padding: '1rem',
                          backgroundColor: story.reviewResult.isGood ? '#d4edda' : '#f8d7da',
                          border: `1px solid ${story.reviewResult.isGood ? '#c3e6cb' : '#f5c6cb'}`,
                          borderRadius: '4px',
                          marginTop: '0.5rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>
                              {story.reviewResult.isGood ? '✅' : '⚠️'}
                            </span>
                            <strong style={{ color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit' }}>
                              {story.reviewResult.status || 'Review Complete'}
                            </strong>
                          </div>
                          <p style={{ margin: '0.5rem 0', color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit', fontSize: '0.95rem' }}>
                            {story.reviewResult.message}
                          </p>
                          {story.reviewResult.issues && story.reviewResult.issues.length > 0 && (
                            <div style={{ marginTop: '0.75rem' }}>
                              <strong style={{ color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit', fontSize: '0.9rem' }}>Issues:</strong>
                              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0, color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit', fontSize: '0.9rem' }}>
                                {story.reviewResult.issues.map((issue, issueIdx) => (
                                  <li key={issueIdx}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {story.reviewResult.recommendations && story.reviewResult.recommendations.length > 0 && (
                            <div style={{ marginTop: '0.75rem' }}>
                              <strong style={{ color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit', fontSize: '0.9rem' }}>Recommendations:</strong>
                              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0, color: story.reviewResult.isGood ? '#155724' : '#721c24', fontFamily: 'inherit', fontSize: '0.9rem' }}>
                                {story.reviewResult.recommendations.map((rec, recIdx) => (
                                  <li key={recIdx}>{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {story.reviewResult.rulesApplied && (
                            <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.8rem', color: '#666', fontFamily: 'inherit', fontStyle: 'italic' }}>
                              Rules Applied: {story.reviewResult.rulesApplied}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addNewStory}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              ➕ Add Another Story
            </button>

            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '1rem', marginBottom: 0 }}>
              Note: These stories will be created after the Epic is successfully created.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
          {/* Debug logging */}
          {(() => {
            console.log('[WORKFLOW-RENDER] availableStatuses.length:', availableStatuses.length);
            console.log('[WORKFLOW-RENDER] mode:', mode);
            console.log('[WORKFLOW-RENDER] selectedIssue:', selectedIssue?.key);
            console.log('[WORKFLOW-RENDER] Should show dropdown:', availableStatuses.length > 0 && (mode === 'add' || (mode === 'edit' && selectedIssue)));
            return null;
          })()}
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            {/* Create/Update Button */}
            <button 
              type="submit" 
              disabled={loading || !isFormEnabled || isReviewing || (mode === 'edit' && !selectedIssue)} 
              style={{
                ...styles.buttonPrimary,
                flex: 1,
                height: '48px', // Fixed height for consistency
                opacity: (loading || !isFormEnabled || isReviewing || (mode === 'edit' && !selectedIssue)) ? 0.7 : 1
              }}
            >
              {mode === 'edit' 
                ? (loading ? `Updating ${issueTypes.find(t => t.id === issueType)?.name || 'Issue'}...` : `✓ Update ${issueTypes.find(t => t.id === issueType)?.name || 'Issue'}`)
                : (loading ? `Creating ${issueTypes.find(t => t.id === issueType)?.name || 'Issue'}...` : `✓ Create ${issueTypes.find(t => t.id === issueType)?.name || 'Issue'}`)
              }
            </button>
            
            {/* Workflow Status Selector - Show in both Add and Edit modes */}
            {availableStatuses.length > 0 && (mode === 'add' || (mode === 'edit' && selectedIssue)) && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.5rem',
                flex: 1
              }}>
                <label htmlFor="workflow-status" style={{ 
                  fontWeight: 'bold', 
                  color: '#333',
                  fontSize: '0.9rem'
                }}>
                  Status:
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  height: '48px' // Match button height
                }}>
                  <select
                    id="workflow-status"
                    value={selectedWorkflowStatus}
                    onChange={(e) => setSelectedWorkflowStatus(e.target.value)}
                    disabled={!isFormEnabled || loading || isReviewing || (mode === 'edit' && !selectedIssue)}
                    style={{
                      flex: 1,
                      height: '100%', // Fill parent height
                      padding: '0 0.75rem', // Adjust padding for fixed height
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      backgroundColor: '#fff',
                      cursor: (!isFormEnabled || loading || isReviewing || (mode === 'edit' && !selectedIssue)) ? 'not-allowed' : 'pointer',
                      opacity: (!isFormEnabled || loading || isReviewing || (mode === 'edit' && !selectedIssue)) ? 0.6 : 1
                    }}
                  >
                    {availableStatuses.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                  {reviewResult && (
                    <span style={{ 
                      fontSize: '1.2rem',
                      marginLeft: '0.25rem'
                    }} title={reviewResult.status === 'approved' ? 'Approved' : 'Needs Work'}>
                      {reviewResult.status === 'approved' ? '✅' : '⚠️'}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Save Without Review Button */}
            <button 
              type="button"
              onClick={handleSaveWithoutReview}
              disabled={loading || !isFormEnabled || isReviewing || (mode === 'edit' && !selectedIssue)} 
              style={{
                ...styles.buttonPrimary,
                backgroundColor: '#6c757d',
                flex: 1,
                height: '48px', // Fixed height for consistency
                opacity: (loading || !isFormEnabled || isReviewing || (mode === 'edit' && !selectedIssue)) ? 0.7 : 1
              }}
              title="Save to JIRA without review checks - you can edit it later"
            >
              {loading ? '⏳ Saving...' : '💾 Save (No Review)'}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div style={styles.error}>
          <h3>Error:</h3>
          <p>{error}</p>
        </div>
      )}

      {result && result.success && (
        <div style={styles.result}>
          <h3>✅ {result.jiraUpdated ? 'Jira Ticket Updated Successfully!' : result.isEpic ? 'Epic and Stories Created Successfully!' : 'Jira Ticket Created Successfully!'}</h3>
          <p><strong>Issue Key:</strong> {result.issueKey}</p>
          <p><a href={result.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
            View {result.issueKey} in Jira →
          </a></p>
          
          {result.isEpic && result.stories && result.stories.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <h4 style={{ marginTop: 0, marginBottom: '0.75rem' }}>✨ Stories Created ({result.stories.length})</h4>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {result.stories.map(story => (
                  <li key={story.key} style={{ marginBottom: '0.5rem' }}>
                    <a href={story.link} target="_blank" rel="noopener noreferrer" style={styles.link}>
                      {story.key}
                    </a> - {story.summary}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>View Generated Payload</summary>
            <pre style={styles.json}>{JSON.stringify(result.payload || result.jiraPayload, null, 2)}</pre>
          </details>
          {result.aiGenerated ? (
            <p style={styles.aiNote}>✨ Generated with AI</p>
          ) : (
            <p style={styles.fallbackNote}>⚡ Standard payload used</p>
          )}
          {result.modelUsed && (
            <p style={{ color: '#888', marginTop: '1rem' }}>
              (Generated with {result.modelUsed})
            </p>
          )}
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {showClearConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            margin: 'auto',
            position: 'relative'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
              Are you sure you want to clear the form?
            </h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              All unsaved changes will be lost.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={cancelClearForm}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClearForm}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Clear Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Description Confirmation Modal */}
      {showClearDescriptionConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            margin: 'auto',
            position: 'relative'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
              Clear entire description?
            </h3>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Are you sure you want to clear the entire description? This action can be undone using the undo button.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowClearDescriptionConfirm(false)}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setDescriptionHistory(description);
                  setDescription('');
                  setShowClearDescriptionConfirm(false);
                }}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Clear Description
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Helper Confirmation Modal */}
      {showAiHelperConfirm && aiHelperConfirmData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            margin: 'auto',
            position: 'relative'
          }}>
            {aiHelperConfirmData.type === 'cached' ? (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#f57c00' }}>
                  ⚠️ AI Helper Already Used
                </h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  You have already run the AI Description Helper for this exact summary.
                </p>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  The same AI-generated content will be <strong>APPENDED</strong> to your existing description.
                </p>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Do you want to continue and add the same AI content again?
                </p>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#f57c00' }}>
                  ⚠️ Summary or Settings Changed
                </h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  The summary, issue type, or AI model has changed since the last AI generation.
                </p>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  A <strong>NEW</strong> AI response will be generated and the previous cached response will be replaced.
                </p>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Do you want to continue?
                </p>
              </>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAiHelperConfirm(false);
                  setAiHelperConfirmData(null);
                }}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => aiHelperConfirmData.onConfirm()}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f57c00',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: 'auto',
            position: 'relative'
          }}>
            {submitConfirmType === 'no-review' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#f57c00' }}>
                  ⚠️ WARNING: Ticket Not Reviewed
                </h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  You are about to submit a ticket without running the Lyric Review process.
                </p>
                <div style={{ backgroundColor: '#fff3e0', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                    Best practice is to:
                  </p>
                  <ol style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
                    <li>Click "🔍 Review" to check for quality issues</li>
                    <li>Fix any issues found (or use Quick Fix)</li>
                    <li>Then submit the ticket</li>
                  </ol>
                </div>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Are you sure you want to submit this UNREVIEWED ticket?
                </p>
              </>
            )}

            {submitConfirmType === 'needs-clarification' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#d32f2f' }}>
                  ⚠️ WARNING: Ticket Not Acceptable for JIRA
                </h3>
                <p style={{ color: '#666', marginBottom: '1rem', fontWeight: '600' }}>
                  Review Status: NEEDS CLARIFICATION
                </p>
                <div style={{ backgroundColor: '#ffebee', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                    Issues Found:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
                    {submitConfirmData?.issues?.slice(0, 3).map((issue, idx) => (
                      <li key={idx}>{issue}</li>
                    ))}
                  </ul>
                </div>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Are you sure you want to submit this non-compliant ticket?
                </p>
              </>
            )}

            {submitConfirmType === 'update' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#0052cc' }}>
                  Update Issue?
                </h3>
                <div style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem' }}>
                  <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666' }}>
                    <strong>Issue:</strong> {submitConfirmData?.issueKey}
                  </p>
                  <p style={{ margin: 0, color: '#666' }}>
                    <strong>Summary:</strong> {submitConfirmData?.summary}
                  </p>
                </div>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Click Update to proceed or Cancel to abort.
                </p>
              </>
            )}

            {submitConfirmType === 'create' && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#0052cc' }}>
                  Create JIRA Issue?
                </h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  The issue will be created with the following reporter:
                </p>
                <div style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem' }}>
                  <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666' }}>
                    <strong>Name:</strong> {submitConfirmData?.reporterName}
                  </p>
                  <p style={{ margin: 0, color: '#666' }}>
                    <strong>Email:</strong> {submitConfirmData?.reporterEmail}
                  </p>
                </div>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                  Click Create to proceed or Cancel to abort.
                </p>
              </>
            )}

            {/* Checkbox to exclude warning data */}
            <div style={{ 
              backgroundColor: '#f9f9f9', 
              padding: '1rem', 
              borderRadius: '4px', 
              marginBottom: '1.5rem',
              border: '1px solid #e0e0e0'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                cursor: 'pointer',
                margin: 0,
                fontWeight: '500',
                color: '#333'
              }}>
                <input 
                  type="checkbox" 
                  checked={excludeWarningData}
                  onChange={(e) => setExcludeWarningData(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#0052cc'
                  }}
                />
                <span>Do NOT add warning/analysis data to ticket</span>
              </label>
              <p style={{ 
                margin: '0.5rem 0 0 2rem', 
                fontSize: '0.875rem', 
                color: '#666',
                fontWeight: 'normal'
              }}>
                If checked, only the issue content will be added (warnings and analysis will be excluded)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={cancelSubmit}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: submitConfirmType === 'no-review' || submitConfirmType === 'needs-clarification' ? '#f57c00' : '#0052cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {submitConfirmType === 'update' ? 'Update' : 
                 submitConfirmType === 'create' ? 'Create' :
                 'Proceed Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Success Modal */}
      {showUpdateSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: 'auto',
            position: 'relative',
            textAlign: 'center'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#28a745' }}>
              ✅ Issue Updated Successfully!
            </h3>
            <div style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px', marginBottom: '1.5rem' }}>
              <p style={{ margin: 0, marginBottom: '0.5rem', color: '#666' }}>
                <strong>Issue:</strong> {updateSuccessData?.issueKey}
              </p>
              <p style={{ margin: 0, color: '#666' }}>
                Your changes have been successfully submitted to JIRA.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setShowUpdateSuccessModal(false);
                  window.open(updateSuccessData?.url, '_blank');
                }}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#0052cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                View in JIRA
              </button>
              <button
                onClick={() => setShowUpdateSuccessModal(false)}
                style={{
                  padding: '0.5rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payload Validation Error Popup */}
      {showValidationError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            margin: 'auto',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '2rem', marginRight: '0.5rem' }}>⚠️</span>
              <h3 style={{ margin: 0, color: '#d32f2f', fontSize: '1.3rem' }}>
                Input Validation Failed
              </h3>
            </div>
            
            <p style={{ color: '#666', marginBottom: '1rem', lineHeight: '1.5' }}>
              Your input appears to have issues. Please review and correct the following:
            </p>
            
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1.5rem',
              maxHeight: '250px',
              overflowY: 'auto'
            }}>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#666' }}>
                {validationErrors.map((err, idx) => (
                  <li key={idx} style={{ marginBottom: '0.5rem' }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
            
            <div style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#666',
              lineHeight: '1.4'
            }}>
              <strong style={{ color: '#333' }}>💡 Tip:</strong> Common issues include:
              <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
                <li>Empty summary or title field</li>
                <li>Empty description field</li>
                <li>Empty list items in the description (try removing them)</li>
                <li>Missing required information</li>
              </ul>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={copyErrorDetailsToClipboard}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2196f3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#1976d2'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#2196f3'}
              >
                📋 Copy Error Details
              </button>
              <button
                onClick={() => setShowValidationError(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              >
                Close
              </button>
            </div>
            
            <p style={{
              fontSize: '0.85rem',
              color: '#999',
              marginTop: '1rem',
              marginBottom: 0,
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              If this issue persists, please copy the error details and send them to the developer for assistance.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

// Set display name for debugging
IssueForm.displayName = 'IssueForm';

const styles = {
  container: {
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '1000px',
    margin: '0 auto',
    color: '#000',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: '700',
    margin: 0,
    color: '#0052cc',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#555',
    marginTop: '0.5rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    marginBottom: '0.25rem',
    color: '#000',
    textAlign: 'left',
  },
  input: {
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    width: '100%',
    textAlign: 'left',
  },
  textarea: {
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    minHeight: '120px',
    resize: 'vertical',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: '#000',
    fontWeight: 'normal',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    boxSizing: 'border-box',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: 'white',
    fontSize: '1rem',
  },
  buttonPrimary: {
    padding: '0.8rem 1.5rem',
    backgroundColor: '#0052cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    width: '100%',
    marginTop: '1rem',
  },
  generateDescButton: {
    width: '30px',
    height: '30px',
    padding: '0',
    backgroundColor: '#6f42c1',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  },
  error: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#fff1f0',
    border: '1px solid #ffa39e',
    borderRadius: '4px',
    color: '#cf1322',
  },
  result: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#e6f7ff',
    border: '1px solid #91d5ff',
    borderRadius: '4px',
  },
  json: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '0.9rem',
    border: '1px solid #e9ecef',
    maxHeight: '200px',
  },
  aiNote: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
    marginTop: '0.5rem',
    color: '#6f42c1',
  },
  fallbackNote: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
    marginTop: '0.5rem',
    color: '#fd7e14',
  },
  link: {
    color: '#0052cc',
    textDecoration: 'none',
    fontWeight: 'bold',
  },
};

export default IssueForm;
